// Extracted from CaseBuddy.vue in refactor pass 2.
// Review-tool methods for inconsistencies, deposits, entities, duplicate hashes, note rewrites, address legend, path operations, attribution, and output-buffer staging.
// These are Options API method definitions and intentionally use `this`.

import { authenticatedFetch } from '../../services/authenticatedFetch.js';

import {
  addEntityCuratorTermToList,
  applyAddressAttributionMappingsToMainMatrix,
  applyCaseNoteHashRewriteItemsToMainMatrix,
  applyDuplicateHashLabelerItemsToMainMatrix,
  applyKnownEntityCasingFixesToMainMatrix,
  buildAddressAttributionAnalysis,
  buildAddressAttributionAuditRows,
  buildAddressAttributionExportTsvFromTargets,
  buildBridgeSearchClipboardText,
  buildBridgeSearchTextFromPathOperationCandidate,
  buildCaseAddressLegendAnalysis,
  buildCaseAddressLegendAuditRows,
  buildCaseAddressLegendDeliverableRows,
  buildCaseAddressLegendEntriesFromCandidates,
  buildCaseAddressLegendQuickAccessRows,
  buildCaseDepositHotWalletAuditRows,
  buildCaseDepositHotWalletReviewAnalysis,
  buildCaseDepositPatternAuditRows,
  buildCaseDepositPatternReviewAnalysis,
  buildCaseDuplicateHashLabelerAnalysis,
  buildCaseEntityCuratorReviewAnalysis,
  buildCaseInconsistencyAuditRows,
  buildCaseInconsistencyReviewAnalysis,
  buildCaseKnownEntityCasingReviewAnalysis,
  buildCaseNoteHashRewriteAnalysis,
  buildCaseNoteHashRewriteAuditRows,
  buildCaseNoteHashRewriteBackupOutputRows,
  buildCaseNoteHashRewriteBackupRecord,
  buildDuplicateHashLabelerAuditRows,
  buildDuplicateHashTableRows,
  buildEntityCuratorAuditRows,
  buildKnownEntityCasingAuditRows,
  buildMainRawDataCoverage,
  buildPathOperationAnalysis,
  buildPathOperationAuditRows,
  buildPathOperationCandidatesTsv,
  buildPathOperationExtensionApplyResult,
  buildPrunedMainDataForCandidate,
  buildRawDataAppendMatrix,
  buildTsv,
  CASE_BUDDY_ARKHAM_NOTE_ENRICHMENT_ENDPOINT,
  CASE_COLUMN_HEADERS,
  CASE_DEFAULT_COLS,
  CASE_DEFAULT_ROWS,
  CASE_DELIVERABLE_GROUPS,
  CASE_DEPOSIT_HOT_WALLET_VENDOR_MAX_LOOKUPS,
  CASE_DEPOSIT_PATTERN_REVIEW_STATUS_DISMISSED,
  CASE_DEPOSIT_PATTERN_REVIEW_STATUS_REVIEWED,
  CASE_ENTITY_CURATOR_REMOTE_EXCHANGE_ENDPOINT,
  CASE_ENTITY_CURATOR_REMOTE_NON_EXCHANGE_ENDPOINT,
  CASE_ENTITY_KIND_EXCHANGE,
  CASE_ENTITY_KIND_NON_EXCHANGE,
  CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_APPLIED,
  CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_DISMISSED,
  CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_REVIEWED,
  classifyCaseDepositHotWalletVendorResult,
  createCaseBridgeServiceMatch,
  downloadTextFile,
  findBestCaseBridgeServiceMatch,
  findExactPathOperationCandidateForRow,
  findPathOperationCandidateForRow,
  formatCaseEntityKindLabel,
  formatKnownEntityCasingReviewStatus,
  loadEntityCuratorOverlayFromStorage,
  mergeCaseNoteHashRewriteBackupRecords,
  normalizeAttributionValue,
  normalizeCaseBridgeServiceNameForStorage,
  normalizeCaseDepositPatternReviewStatus,
  normalizeCaseEntityTerm,
  normalizeCaseMatrix,
  normalizeCell,
  normalizeComparisonKey,
  normalizeEntityCuratorList,
  normalizeEntityCuratorOverlay,
  normalizeKnownEntityCasingReviewStatus,
  parseAddressAttributionTsv,
  removeEntityCuratorTermFromList,
  restoreCaseNoteHashRewriteBackupToMainMatrix,
  rowHasMeaningfulData,
  saveEntityCuratorOverlayToStorage,
  serializeCaseBridgeServiceMatch,
  sleep,
  WORKFLOW_SIGNAL_ADDRESS_LEGEND_REVIEWED,
  WORKFLOW_SIGNAL_DEPOSIT_HOT_WALLET_NOTES_NORMALIZED,
  WORKFLOW_SIGNAL_DEPOSIT_PATTERNS_REVIEWED,
  WORKFLOW_SIGNAL_DUPLICATE_HASHES_LABELED,
  WORKFLOW_SIGNAL_KNOWN_ENTITY_CASING_REVIEWED,
  WORKFLOW_SIGNAL_NEW_ENTITIES_REVIEWED,
  WORKFLOW_SIGNAL_NOTE_HASH_PATH_LABEL_REWRITE_COMPLETE,
  WORKFLOW_SIGNAL_PATH_OPERATION_RESOLVED,
  WORKFLOW_SIGNAL_TO_ADDRESS_ATTRIBUTION_APPLIED
} from '../CaseBuddyCore.js';

export const caseBuddyReviewToolMethods = {
    async openCaseInconsistencyReviewModal() {
      return this.runCaseModalOperation({
        title: 'Opening inconsistency review…',
        message: 'Scanning current Main Data for review cards.',
        target: 'case-inconsistency-modal',
        finishMessage: 'Inconsistency review ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.caseInconsistencyViewFilter = 'pending';
        this.caseInconsistencySearch = '';
        this.showCaseInconsistencyModal = true;
        await this.yieldToCaseUi();
        this.refreshCaseInconsistencyReview({ silent: true });
        return true;
      });
    },

    cancelCaseInconsistencyReviewModal() {
      if (this.caseInconsistencyRunning) return;
      this.showCaseInconsistencyModal = false;
    },

    buildCaseInconsistencyReviewCheckResult(analysis = null) {
      const reviewAnalysis = analysis || buildCaseInconsistencyReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState.inconsistencyReview,
        this.caseInconsistencyRuleOptions
      );
      if ((reviewAnalysis.itemCount || 0) === 0) {
        return this.buildCheckResult('case_inconsistencies', 'pass', 'No Tool 71-style inconsistency cards were found with the enabled checks.', reviewAnalysis);
      }
      if ((reviewAnalysis.pendingCount || 0) === 0) {
        return this.buildCheckResult(
          'case_inconsistencies',
          'pass',
          `${reviewAnalysis.itemCount} inconsistency card${reviewAnalysis.itemCount === 1 ? '' : 's'} ${reviewAnalysis.itemCount === 1 ? 'has' : 'have'} been resolved or acknowledged for the current scan.`,
          reviewAnalysis
        );
      }
      return this.buildCheckResult(
        'case_inconsistencies',
        'fail',
        `${reviewAnalysis.pendingCount} inconsistency card${reviewAnalysis.pendingCount === 1 ? '' : 's'} still need review. Open Review Cards, apply safe fixes, and mark judgement cards resolved when appropriate.`,
        reviewAnalysis
      );
    },

    refreshCaseInconsistencyReview(options = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const analysis = buildCaseInconsistencyReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState.inconsistencyReview,
        this.caseInconsistencyRuleOptions
      );
      this.caseInconsistencyCards = analysis.items || [];
      this.caseInconsistencyLastRunSummary = analysis;
      if (!options.silent) {
        this.caseInconsistencyStatus = analysis.summary;
      } else if (!this.caseInconsistencyStatus) {
        this.caseInconsistencyStatus = analysis.summary;
      }
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        case_inconsistencies: this.buildCaseInconsistencyReviewCheckResult(analysis)
      };
      this.persistCaseSupportState();
      this.queueCaseSummaryRefresh();
      return analysis;
    },

    selectCaseInconsistencyCardRow(card) {
      if (!card || !Number.isInteger(card.rowIndex)) return false;
      this.activateLogicalSheet('main');
      this.$nextTick(() => {
        const sheet = this.getInputSheet?.();
        sheet?.selectCell?.(card.rowIndex, 0);
        sheet?.hotInstance?.scrollViewportTo?.(card.rowIndex, 0, true, true);
      });
      return true;
    },

    markCaseInconsistencyCardResolved(card, resolved = true) {
      if (!card?.key) return null;
      const existingReview = this.caseSupportState.workflowState.inconsistencyReview && typeof this.caseSupportState.workflowState.inconsistencyReview === 'object'
        ? this.caseSupportState.workflowState.inconsistencyReview
        : {};
      const resolvedCardKeys = { ...(existingReview.resolvedCardKeys || {}) };
      if (resolved) {
        resolvedCardKeys[card.key] = {
          resolvedAtUtc: new Date().toISOString(),
          revision: Number(this.caseSupportState.caseDataRevision) || 0,
          rowNum: card.rowNum,
          entryId: card.entryId,
          issueCount: card.issueCount,
          ruleTags: Array.isArray(card.ruleTags) ? card.ruleTags.slice() : []
        };
      } else {
        delete resolvedCardKeys[card.key];
      }
      this.caseSupportState.workflowState.inconsistencyReview = {
        ...existingReview,
        resolvedCardKeys,
        lastTouchedUtc: new Date().toISOString()
      };
      this.persistCaseSupportState();
      const localCard = this.caseInconsistencyCards.find(item => item.key === card.key);
      if (localCard) localCard.isResolved = !!resolved;
      const analysis = this.refreshCaseInconsistencyReview({ silent: true });
      this.caseInconsistencyStatus = resolved ? 'Card marked resolved for the current issue signature.' : 'Card returned to pending.';
      return analysis;
    },

    markVisibleCaseInconsistencyCardsResolved() {
      const visible = this.caseInconsistencyVisibleCards.filter(card => !card.isResolved);
      if (visible.length === 0) {
        this.showCaseToast('No visible pending inconsistency cards to mark resolved.');
        return null;
      }
      const confirmed = window.confirm(`Mark ${visible.length} visible inconsistency card${visible.length === 1 ? '' : 's'} resolved without changing Main Data?`);
      if (!confirmed) return null;
      visible.forEach(card => this.markCaseInconsistencyCardResolved(card, true));
      const analysis = this.refreshCaseInconsistencyReview({ silent: true });
      this.caseInconsistencyStatus = `${visible.length} visible card${visible.length === 1 ? '' : 's'} marked resolved.`;
      return analysis;
    },

    applyCaseInconsistencyFix(fix, card = null) {
      if (!fix || !Number.isInteger(fix.targetRowIndex) || !Number.isInteger(fix.colIndex)) {
        this.showCaseToast('This inconsistency fix is missing a valid target cell.');
        return null;
      }
      if (fix.kind !== 'safe') {
        const confirmed = window.confirm(`Apply judgement fix to row ${fix.targetRowNum}, ${fix.fieldLabel}?\n\n${fix.reason || 'Review the change before applying.'}`);
        if (!confirmed) return null;
      }
      return this.applyCaseInconsistencyFixes([fix], card ? `Applied fix for row ${card.rowNum}` : 'Applied inconsistency fix');
    },

    applyAllSafeCaseInconsistencyFixes() {
      const fixes = [];
      this.caseInconsistencyCards.forEach((card) => {
        if (card.isResolved) return;
        (card.fixes || []).forEach((fix) => {
          if (fix.kind === 'safe') fixes.push(fix);
        });
      });
      if (fixes.length === 0) {
        this.showCaseToast('No pending safe inconsistency fixes are available.');
        return null;
      }
      return this.applyCaseInconsistencyFixes(fixes, `Applied ${fixes.length} safe inconsistency fix${fixes.length === 1 ? '' : 'es'}`);
    },

    applyCaseInconsistencyFixes(fixes = [], actionLabel = 'Applied inconsistency fixes') {
      if (!Array.isArray(fixes) || fixes.length === 0) return null;
      this.caseInconsistencyRunning = true;
      try {
        const mainSheet = this.findLogicalSheetMeta('main');
        if (!mainSheet?.id) throw new Error('Could not find Main Data.');
        const state = this.getSheetStateById(mainSheet.id);
        const data = normalizeCaseMatrix(state.data);
        const applied = [];
        const skipped = [];
        const seenTargets = new Set();
        fixes.forEach((fix) => {
          if (!Number.isInteger(fix.targetRowIndex) || !Number.isInteger(fix.colIndex)) {
            skipped.push({ fix, reason: 'Invalid target cell.' });
            return;
          }
          const targetKey = `${fix.targetRowIndex},${fix.colIndex}`;
          if (seenTargets.has(targetKey)) {
            skipped.push({ fix, reason: 'Another fix already targeted this cell in this batch.' });
            return;
          }
          seenTargets.add(targetKey);
          if (!Array.isArray(data[fix.targetRowIndex])) {
            skipped.push({ fix, reason: `Row ${fix.targetRowNum} no longer exists.` });
            return;
          }
          const currentValue = normalizeCell(data[fix.targetRowIndex][fix.colIndex]);
          if (currentValue === normalizeCell(fix.newValue)) {
            skipped.push({ fix, reason: 'Target already has the proposed value.' });
            return;
          }
          data[fix.targetRowIndex][fix.colIndex] = normalizeCell(fix.newValue);
          applied.push({ fix, oldValue: currentValue, newValue: normalizeCell(fix.newValue) });
        });
        if (applied.length === 0) {
          this.caseInconsistencyStatus = skipped.length ? `No fixes applied. ${skipped[0].reason}` : 'No fixes applied.';
          return { applied, skipped };
        }
        this.writeSheetStateById(mainSheet.id, { ...state, data });
        this.markCaseDataMutated('case_inconsistency_fixes_applied', {
          applied: applied.length,
          skipped: skipped.length,
          actionLabel
        });
        const analysis = this.refreshCaseInconsistencyReview({ silent: true });
        const auditRows = [
          ['Case Buddy Inconsistency Fix Audit', '', '', '', '', '', '', '', '', 'Generated by Review and resolve case inconsistencies. Main Data was updated.'],
          ['Action', actionLabel, 'Applied', applied.length, 'Skipped', skipped.length, 'Remaining Cards', analysis.pendingCount || 0],
          ['Kind', 'Code', 'Row', 'Column', 'Old Value', 'New Value', 'Reason']
        ];
        applied.forEach(({ fix, oldValue, newValue }) => auditRows.push([fix.kind, fix.code, fix.targetRowNum, fix.fieldLabel, oldValue, newValue, fix.reason || '']));
        skipped.forEach(({ fix, reason }) => auditRows.push([fix?.kind || '', fix?.code || '', fix?.targetRowNum || '', fix?.fieldLabel || '', '', fix?.newValue || '', reason]));
        this.loadOutputRows(auditRows, {
          title: 'Case inconsistency fix audit',
          detail: `${applied.length} inconsistency fix${applied.length === 1 ? '' : 'es'} applied to Main Data.`,
          sourceActionId: 'review_case_inconsistencies',
          target: 'audit-only'
        });
        this.caseInconsistencyStatus = `${applied.length} fix${applied.length === 1 ? '' : 'es'} applied. ${analysis.pendingCount || 0} card${(analysis.pendingCount || 0) === 1 ? '' : 's'} still pending.`;
        if ((analysis.pendingCount || 0) === 0) {
          this.recordWorkflowSignal('CASE_INCONSISTENCIES_REVIEWED', 'case_inconsistency_fixes_applied', { silent: true });
        }
        return { applied, skipped, analysis };
      } catch (error) {
        this.caseInconsistencyStatus = error?.message || 'Failed to apply inconsistency fix.';
        return null;
      } finally {
        this.caseInconsistencyRunning = false;
      }
    },

    markCaseInconsistencyReviewComplete() {
      const analysis = this.refreshCaseInconsistencyReview({ silent: true });
      if ((analysis.pendingCount || 0) > 0) {
        this.caseInconsistencyStatus = `${analysis.pendingCount} card${analysis.pendingCount === 1 ? '' : 's'} still need resolution before this modal can mark the review complete. Use manual workflow completion if you intentionally need to override.`;
        return false;
      }
      this.caseSupportState.workflowState.inconsistencyReview = {
        ...(this.caseSupportState.workflowState.inconsistencyReview || {}),
        basisHash: analysis.currentBasisHash,
        completedAtUtc: new Date().toISOString(),
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        itemCount: analysis.itemCount || 0,
        pendingCount: analysis.pendingCount || 0,
        totalIssueCount: analysis.totalIssueCount || 0,
        resolvedCardKeys: {
          ...(this.caseSupportState.workflowState.inconsistencyReview?.resolvedCardKeys || {})
        }
      };
      this.recordWorkflowSignal('CASE_INCONSISTENCIES_REVIEWED', 'review_case_inconsistencies', { silent: true });
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        case_inconsistencies: this.buildCaseInconsistencyReviewCheckResult(analysis)
      };
      this.persistCaseSupportState();
      this.loadOutputRows(buildCaseInconsistencyAuditRows(analysis, 'Review confirmed'), {
        title: 'Case inconsistency review audit',
        detail: 'Inconsistency review was marked complete for the current scan.',
        sourceActionId: 'review_case_inconsistencies',
        target: 'audit-only'
      });
      this.caseInconsistencyStatus = 'Case inconsistency review marked complete for the current scan.';
      this.showCaseToast('Case inconsistency review marked complete.');
      return true;
    },

    async openDepositHotWalletModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening deposit/hot-wallet review…',
        message: 'Scanning Main Data for note-normalization candidates.',
        target: 'deposit-hot-wallet-modal',
        finishMessage: 'Deposit/hot-wallet review ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.depositHotWalletSearch = '';
        this.depositHotWalletViewFilter = options?.view || 'eligible';
        this.depositHotWalletStatus = 'Scanning Main Data for safe note normalization candidates.';
        this.depositHotWalletLastRunSummary = null;
        this.entityCuratorLocalOverlay = loadEntityCuratorOverlayFromStorage();
        this.clearCasePerformanceCache?.();
        if (this.entityCuratorRemote.status === 'idle' || options?.forceRemote) {
          await this.loadEntityCuratorRemoteKeywords({ force: !!options?.forceRemote, silent: true });
        }
        this.showDepositHotWalletModal = true;
        await this.yieldToCaseUi();
        this.refreshDepositHotWalletReview({ silent: true, resetSelection: true });
        return true;
      });
    },

    cancelDepositHotWalletModal() {
      if (this.depositHotWalletRunning) return;
      this.showDepositHotWalletModal = false;
    },

    refreshDepositHotWalletReview(options = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      this.entityCuratorLocalOverlay = loadEntityCuratorOverlayFromStorage();
      this.clearCasePerformanceCache?.();
      const analysis = buildCaseDepositHotWalletReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        this.depositHotWalletVendorResultsByKey
      );
      this.depositHotWalletLastRunSummary = analysis;
      if (options.resetSelection) {
        const selection = {};
        (analysis.localCandidates || []).forEach((candidate) => {
          if (candidate.isEligible) selection[candidate.key] = true;
        });
        (analysis.vendorCandidates || []).forEach((candidate) => {
          selection[candidate.key] = !!candidate.isEligible;
        });
        this.depositHotWalletSelectionByKey = selection;
      }
      if (!options.silent) this.depositHotWalletStatus = analysis.summary;
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        deposit_hot_wallet_notes: this.buildDepositHotWalletReviewCheckResult(analysis)
      };
      this.persistCaseSupportState();
      return analysis;
    },

    buildDepositHotWalletReviewCheckResult(analysis = null) {
      const reviewAnalysis = analysis || buildCaseDepositHotWalletReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        this.depositHotWalletVendorResultsByKey
      );
      if ((reviewAnalysis.eligibleLocalCount || 0) > 0) {
        return this.buildCheckResult(
          'deposit_hot_wallet_notes',
          'fail',
          `${reviewAnalysis.eligibleLocalCount} safe note normalization candidate${reviewAnalysis.eligibleLocalCount === 1 ? '' : 's'} remain. Open Normalize Notes and apply or review them.`,
          reviewAnalysis
        );
      }
      return this.buildCheckResult(
        'deposit_hot_wallet_notes',
        'pass',
        (reviewAnalysis.vendorCandidateCount || 0) > 0
          ? `No safe local normalizations remain. ${reviewAnalysis.vendorCandidateCount} optional Arkham lookup candidate${reviewAnalysis.vendorCandidateCount === 1 ? '' : 's'} can still be reviewed from the modal if desired.`
          : 'No safe note normalization candidates remain.',
        reviewAnalysis
      );
    },

    isDepositHotWalletCandidateSelected(key) {
      return !!this.depositHotWalletSelectionByKey?.[key];
    },

    setDepositHotWalletCandidateSelection(key, selected) {
      if (!key) return;
      this.depositHotWalletSelectionByKey = {
        ...(this.depositHotWalletSelectionByKey || {}),
        [key]: !!selected
      };
    },

    setDepositHotWalletVisibleSelection(selected) {
      const next = { ...(this.depositHotWalletSelectionByKey || {}) };
      (this.depositHotWalletVisibleCandidates || []).forEach((candidate) => {
        next[candidate.key] = !!selected;
      });
      this.depositHotWalletSelectionByKey = next;
    },

    selectDepositHotWalletConfirmedVendorCandidates() {
      const next = { ...(this.depositHotWalletSelectionByKey || {}) };
      let selected = 0;
      (this.depositHotWalletAnalysis.vendorCandidates || []).forEach((candidate) => {
        if (!candidate.isEligible) return;
        next[candidate.key] = true;
        selected += 1;
      });
      this.depositHotWalletSelectionByKey = next;
      this.depositHotWalletViewFilter = selected > 0 ? 'confirmed_vendor' : this.depositHotWalletViewFilter;
      this.depositHotWalletStatus = selected > 0
        ? `${selected} confirmed Arkham suggestion${selected === 1 ? '' : 's'} selected and ready to apply.`
        : 'No confirmed Arkham suggestions are currently ready to apply.';
      return selected;
    },

    selectDepositHotWalletCandidateRow(candidate) {
      if (!candidate) return;
      const rowIndex = Number.isInteger(candidate.rowIndex)
        ? candidate.rowIndex
        : Number.isInteger(candidate.entry1RowIndex)
          ? candidate.entry1RowIndex
          : null;
      if (!Number.isInteger(rowIndex)) return;
      this.activateLogicalSheet('main');
      this.$nextTick(() => {
        const sheet = this.getInputSheet?.();
        sheet?.selectCell?.(rowIndex, 9);
        sheet?.hotInstance?.scrollViewportTo?.(rowIndex, 9, true, true);
      });
    },

    applyDepositHotWalletCandidatesToMain(candidates = [], sourceAction = 'normalize_deposit_hot_wallet_notes') {
      const mainSheet = this.findLogicalSheetMeta('main');
      if (!mainSheet || !Array.isArray(candidates) || candidates.length === 0) {
        return { changed: false, rowsUpdated: 0, pairsUpdated: 0 };
      }
      const state = this.getSheetStateById(mainSheet.id);
      const data = normalizeCaseMatrix(state.data);
      let rowsUpdated = 0;
      let pairsUpdated = 0;
      let changed = false;
      const touchedRows = new Set();

      candidates.forEach((candidate) => {
        if (!candidate || !candidate.isEligible) return;
        if (candidate.type === 'pair') {
          const updates = [
            { rowIndex: candidate.entry1RowIndex, value: candidate.entry1ProposedNotes },
            { rowIndex: candidate.entry2RowIndex, value: candidate.entry2ProposedNotes }
          ];
          let changedPair = false;
          updates.forEach((update) => {
            if (!Number.isInteger(update.rowIndex) || !update.value) return;
            while (data.length <= update.rowIndex) data.push(Array(CASE_DEFAULT_COLS).fill(''));
            const row = Array.isArray(data[update.rowIndex]) ? data[update.rowIndex] : Array(CASE_DEFAULT_COLS).fill('');
            data[update.rowIndex] = row.length >= CASE_DEFAULT_COLS ? row : [...row, ...Array(CASE_DEFAULT_COLS - row.length).fill('')];
            if (normalizeCell(data[update.rowIndex][9]) !== update.value) {
              data[update.rowIndex][9] = update.value;
              changed = true;
              changedPair = true;
              touchedRows.add(update.rowIndex);
            }
          });
          if (changedPair) pairsUpdated += 1;
          return;
        }

        if (!Number.isInteger(candidate.rowIndex) || !candidate.proposedNotes) return;
        while (data.length <= candidate.rowIndex) data.push(Array(CASE_DEFAULT_COLS).fill(''));
        const row = Array.isArray(data[candidate.rowIndex]) ? data[candidate.rowIndex] : Array(CASE_DEFAULT_COLS).fill('');
        data[candidate.rowIndex] = row.length >= CASE_DEFAULT_COLS ? row : [...row, ...Array(CASE_DEFAULT_COLS - row.length).fill('')];
        if (normalizeCell(data[candidate.rowIndex][9]) !== candidate.proposedNotes) {
          data[candidate.rowIndex][9] = candidate.proposedNotes;
          changed = true;
          rowsUpdated += 1;
          touchedRows.add(candidate.rowIndex);
        }
      });

      if (!changed) return { changed: false, rowsUpdated: 0, pairsUpdated: 0 };
      state.data = data;
      this.writeSheetStateById(mainSheet.id, state);
      this.markCaseDataMutated(sourceAction, {
        rowsUpdated: touchedRows.size || rowsUpdated,
        pairsUpdated
      });
      this.activateLogicalSheet('main');
      return { changed: true, rowsUpdated: touchedRows.size || rowsUpdated, pairsUpdated };
    },

    applySelectedDepositHotWalletSafeNormalizations() {
      const selected = (this.depositHotWalletAnalysis.localCandidates || []).filter(candidate => candidate.isEligible && this.isDepositHotWalletCandidateSelected(candidate.key));
      if (selected.length === 0) {
        this.depositHotWalletStatus = 'No selected safe local normalization candidates are ready to apply.';
        return false;
      }
      const result = this.applyDepositHotWalletCandidatesToMain(selected, 'deposit_hot_wallet_notes_normalized');
      const analysis = this.refreshDepositHotWalletReview({ silent: true, resetSelection: true });
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        deposit_hot_wallet_notes: this.buildDepositHotWalletReviewCheckResult(analysis)
      };
      if ((analysis.eligibleLocalCount || 0) === 0) {
        this.recordWorkflowSignal(WORKFLOW_SIGNAL_DEPOSIT_HOT_WALLET_NOTES_NORMALIZED, 'normalize_deposit_hot_wallet_notes', { silent: true });
      }
      this.loadOutputRows(buildCaseDepositHotWalletAuditRows(analysis, 'Safe local normalizations applied'), {
        title: 'Deposit / hot wallet note normalization audit',
        detail: 'Audit only. Main Data Column J was updated by the safe local normalizer.',
        sourceActionId: 'normalize_deposit_hot_wallet_notes',
        target: 'audit-only'
      });
      this.depositHotWalletStatus = result.changed
        ? `Applied ${selected.length} selected safe normalization candidate${selected.length === 1 ? '' : 's'}. ${analysis.eligibleLocalCount || 0} safe candidate${(analysis.eligibleLocalCount || 0) === 1 ? '' : 's'} remain.`
        : 'Selected candidates did not change Main Data.';
      this.showCaseToast(this.depositHotWalletStatus);
      return result;
    },

    async postCaseBuddyVendorJson(endpoint, payload = {}) {
      const requestedEndpoint = normalizeCell(endpoint);
      let parsedEndpoint = null;

      try {
        parsedEndpoint = new URL(requestedEndpoint, window.location.origin);
      } catch {
        throw new Error('The Arkham endpoint configuration is invalid.');
      }

      const approvedPath = '/arkham/arkham_note_enrichment_batch.php';
      const approvedSourcePaths = new Set([
        approvedPath,
        '/api/arkham/arkham_note_enrichment_batch.php'
      ]);
      const approvedSourceOrigins = new Set([
        window.location.origin,
        'https://mortalitycore.com',
        'https://www.mortalitycore.com'
      ]);

      if (
        !approvedSourceOrigins.has(parsedEndpoint.origin) ||
        !approvedSourcePaths.has(parsedEndpoint.pathname) ||
        parsedEndpoint.search ||
        parsedEndpoint.hash
      ) {
        throw new Error('The Arkham endpoint is not approved for authenticated Case Buddy use.');
      }

      // Always dispatch through the current QA origin. This accepts older
      // absolute endpoint constants without sending the bearer token across
      // origins.
      const response = await authenticatedFetch(approvedPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...(payload || {}),
          client_context: 'casebuddy',
          return_raw_response: false
        })
      });

      const rawText = await response.text();
      let data = null;
      try {
        data = rawText ? JSON.parse(rawText) : null;
      } catch {
        const requestId = response.headers.get('X-Request-ID') || '';
        throw new Error(
          requestId
            ? `The Arkham endpoint returned an invalid response. (Request ${requestId})`
            : 'The Arkham endpoint returned an invalid response.'
        );
      }

      if (!response.ok || !data || data.status === 'error') {
        const requestId =
          response.headers.get('X-Request-ID') ||
          data?.request_id ||
          data?.requestId ||
          '';
        const message =
          data?.message ||
          data?.error ||
          `Arkham request failed with HTTP ${response.status}.`;
        throw new Error(
          requestId ? `${message} (Request ${requestId})` : message
        );
      }

      return data;
    },

    async runDepositHotWalletVendorLookup() {
      if (this.depositHotWalletRunning) return false;
      if (!this.depositHotWalletVendorLookupEnabled) {
        this.depositHotWalletStatus = 'Vendor lookup is currently disabled for this modal.';
        return false;
      }
      const candidates = (this.depositHotWalletAnalysis.vendorCandidates || []).filter(candidate => this.isDepositHotWalletCandidateSelected(candidate.key) && !candidate.vendorClassification);
      if (candidates.length === 0) {
        this.depositHotWalletStatus = 'No selected Arkham lookup candidates need lookup.';
        return false;
      }
      const uniqueAddresses = [...new Map(candidates.map(candidate => [normalizeComparisonKey(candidate.toAddress), candidate.toAddress])).values()].filter(Boolean);
      if (uniqueAddresses.length === 0) {
        this.depositHotWalletStatus = 'Selected lookup candidates do not have Column D receiving addresses.';
        return false;
      }

      this.depositHotWalletRunning = true;
      this.depositHotWalletStatus = `Looking up ${uniqueAddresses.length} unique receiving address${uniqueAddresses.length === 1 ? '' : 'es'} with Arkham…`;
      try {
        const payload = {
          addresses: uniqueAddresses,
          cache_first: true,
          require_fresh_cache: !!this.depositHotWalletRequireFreshCache,
          ttl_hours: Math.max(1, Number(this.depositHotWalletTtlHours) || 48),
          max_vendor_requests: CASE_DEPOSIT_HOT_WALLET_VENDOR_MAX_LOOKUPS,
          return_raw_response: false
        };
        const json = await this.postCaseBuddyVendorJson(CASE_BUDDY_ARKHAM_NOTE_ENRICHMENT_ENDPOINT, payload);
        const resultMap = new Map();
        (Array.isArray(json.results) ? json.results : []).forEach((result) => {
          const address = normalizeCell(result?.address || result?.input_address || result?.query_address || result?.wallet_address);
          const key = normalizeComparisonKey(address);
          if (key) resultMap.set(key, result);
        });
        const nextResults = { ...(this.depositHotWalletVendorResultsByKey || {}) };
        let confirmed = 0;
        let unclassified = 0;
        let missing = 0;
        candidates.forEach((candidate) => {
          const result = resultMap.get(normalizeComparisonKey(candidate.toAddress));
          if (!result) {
            missing += 1;
            nextResults[candidate.key] = { error: 'No result returned for this address.' };
            return;
          }
          const classification = classifyCaseDepositHotWalletVendorResult(result);
          if (classification) confirmed += 1;
          else unclassified += 1;
          nextResults[candidate.key] = {
            classification,
            address: candidate.toAddress,
            fetchedAtUtc: new Date().toISOString(),
            raw: result,
            error: normalizeCell(result?.error)
          };
        });
        this.depositHotWalletVendorResultsByKey = nextResults;
        const analysis = this.refreshDepositHotWalletReview({ silent: true });
        if (confirmed > 0) {
          const nextSelection = { ...(this.depositHotWalletSelectionByKey || {}) };
          (analysis.vendorCandidates || []).forEach((candidate) => {
            if (candidate.isEligible) nextSelection[candidate.key] = true;
          });
          this.depositHotWalletSelectionByKey = nextSelection;
          this.depositHotWalletViewFilter = 'confirmed_vendor';
        }
        this.depositHotWalletStatus = confirmed > 0
          ? `Arkham lookup complete: ${confirmed} confirmed and auto-selected for application, ${unclassified} unclassified, ${missing} missing result${missing === 1 ? '' : 's'}. Use “Apply all confirmed” or review the selected cards first.`
          : `Arkham lookup complete: ${confirmed} confirmed, ${unclassified} unclassified, ${missing} missing result${missing === 1 ? '' : 's'}.`;
        this.loadOutputRows(buildCaseDepositHotWalletAuditRows(analysis, 'Arkham lookup staged'), {
          title: 'Deposit / hot wallet Arkham lookup audit',
          detail: 'Audit only. Confirmed vendor rows are staged in the modal until applied to Main Data.',
          sourceActionId: 'lookup_deposit_hot_wallet_arkham',
          target: 'audit-only'
        });
        return { ok: true, confirmed, unclassified, missing, analysis };
      } catch (error) {
        this.depositHotWalletStatus = error?.message || 'Arkham lookup failed.';
        this.showCaseToast(this.depositHotWalletStatus);
        return { ok: false, status: this.depositHotWalletStatus };
      } finally {
        this.depositHotWalletRunning = false;
      }
    },

    applyDepositHotWalletVendorCandidate(candidate) {
      if (!candidate?.isEligible) {
        this.depositHotWalletStatus = 'This Arkham suggestion is not confirmed and ready to apply.';
        return false;
      }
      this.setDepositHotWalletCandidateSelection(candidate.key, true);
      return this.applyDepositHotWalletVendorCandidates([candidate], 'single confirmed Arkham suggestion', { skipConfirm: true });
    },

    applyAllConfirmedDepositHotWalletVendorNormalizations() {
      const confirmed = (this.depositHotWalletAnalysis.vendorCandidates || []).filter(candidate => candidate.isEligible);
      if (confirmed.length === 0) {
        this.depositHotWalletStatus = 'No confirmed Arkham suggestions are ready to apply.';
        return false;
      }
      confirmed.forEach((candidate) => this.setDepositHotWalletCandidateSelection(candidate.key, true));
      return this.applyDepositHotWalletVendorCandidates(confirmed, 'all confirmed Arkham suggestion');
    },

    applySelectedDepositHotWalletVendorNormalizations() {
      const selected = (this.depositHotWalletAnalysis.vendorCandidates || []).filter(candidate => candidate.isEligible && this.isDepositHotWalletCandidateSelected(candidate.key));
      if (selected.length === 0) {
        this.depositHotWalletStatus = 'No confirmed selected Arkham results are ready to apply.';
        return false;
      }
      return this.applyDepositHotWalletVendorCandidates(selected, 'selected confirmed Arkham suggestion');
    },

    applyDepositHotWalletVendorCandidates(selected = [], sourceLabel = 'confirmed Arkham suggestion', options = {}) {
      const candidates = (Array.isArray(selected) ? selected : []).filter(candidate => candidate?.isEligible);
      if (candidates.length === 0) {
        this.depositHotWalletStatus = 'No confirmed Arkham suggestions are ready to apply.';
        return false;
      }
      if (!options.skipConfirm && candidates.length > 1) {
        const confirmed = window.confirm(`Apply ${candidates.length} confirmed Arkham deposit/hot-wallet note suggestion${candidates.length === 1 ? '' : 's'} to Main Data Column J?`);
        if (!confirmed) return false;
      }
      const result = this.applyDepositHotWalletCandidatesToMain(candidates, 'deposit_hot_wallet_vendor_notes_applied');
      const analysis = this.refreshDepositHotWalletReview({ silent: true, resetSelection: true });
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        deposit_hot_wallet_notes: this.buildDepositHotWalletReviewCheckResult(analysis)
      };
      this.loadOutputRows(buildCaseDepositHotWalletAuditRows(analysis, 'Confirmed Arkham results applied'), {
        title: 'Deposit / hot wallet Arkham application audit',
        detail: 'Audit only. Confirmed Arkham classifications were applied to Main Data Column J.',
        sourceActionId: 'apply_deposit_hot_wallet_arkham_notes',
        target: 'audit-only'
      });
      this.depositHotWalletStatus = result.changed
        ? `Applied ${candidates.length} ${sourceLabel}${candidates.length === 1 ? '' : 's'} to Main Data Column J.`
        : `${candidates.length} ${sourceLabel}${candidates.length === 1 ? '' : 's'} did not change Main Data.`;
      this.showCaseToast(this.depositHotWalletStatus);
      return result;
    },

    markDepositHotWalletReviewComplete() {
      const analysis = this.refreshDepositHotWalletReview({ silent: true });
      if ((analysis.eligibleLocalCount || 0) > 0) {
        this.depositHotWalletStatus = `${analysis.eligibleLocalCount} safe candidate${analysis.eligibleLocalCount === 1 ? '' : 's'} still remain. Apply them, or use manual workflow completion outside this modal if you intentionally need to override.`;
        return false;
      }
      this.caseSupportState.workflowState.depositHotWalletReview = {
        basisHash: analysis.basisHash,
        completedAtUtc: new Date().toISOString(),
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        eligibleLocalCount: analysis.eligibleLocalCount || 0,
        vendorCandidateCount: analysis.vendorCandidateCount || 0,
        confirmedVendorCount: analysis.confirmedVendorCount || 0
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_DEPOSIT_HOT_WALLET_NOTES_NORMALIZED, 'normalize_deposit_hot_wallet_notes', { silent: true });
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        deposit_hot_wallet_notes: this.buildDepositHotWalletReviewCheckResult(analysis)
      };
      this.persistCaseSupportState();
      this.loadOutputRows(buildCaseDepositHotWalletAuditRows(analysis, 'Normalization review confirmed'), {
        title: 'Deposit / hot wallet note normalization audit',
        detail: 'Audit only. Deposit/hot-wallet normalization review was marked complete for the current scan.',
        sourceActionId: 'normalize_deposit_hot_wallet_notes',
        target: 'audit-only'
      });
      this.depositHotWalletStatus = 'Deposit / hot wallet normalization review marked complete.';
      this.showCaseToast(this.depositHotWalletStatus);
      return true;
    },

    async openDepositPatternReviewModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening deposit-pattern review…',
        message: 'Finding high-priority deposit-pattern candidates.',
        target: 'deposit-pattern-modal',
        finishMessage: 'Deposit-pattern review ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.depositPatternSearch = '';
        this.depositPatternViewFilter = options?.view || 'pending_required';
        this.depositPatternMinGrade = options?.minGrade || 'HIGH';
        this.depositPatternSortMode = options?.sort || 'priority_then_time';
        this.depositPatternStatus = 'Scanning Main Data for Tool 78-style deposit-pattern candidates.';
        this.depositPatternLastRunSummary = null;
        this.showDepositPatternModal = true;
        await this.yieldToCaseUi();
        this.refreshDepositPatternReview({ silent: true });
        return true;
      });
    },

    cancelDepositPatternReviewModal() {
      this.showDepositPatternModal = false;
    },

    buildDepositPatternReviewCheckResult(analysis = null) {
      const reviewAnalysis = analysis || buildCaseDepositPatternReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
      );

      if ((reviewAnalysis.requiredCandidateCount || 0) === 0) {
        return this.buildCheckResult(
          'deposit_patterns',
          'pass',
          reviewAnalysis.totalCandidateCount > 0
            ? `No HIGH/VERY_HIGH deposit-pattern candidates require review. ${reviewAnalysis.optionalCandidateCount} optional lower-priority candidate${reviewAnalysis.optionalCandidateCount === 1 ? '' : 's'} remain available in the modal.`
            : 'No Tool 78-style deposit-pattern / early path-end candidates were found.',
          reviewAnalysis
        );
      }

      if ((reviewAnalysis.pendingRequiredCount || 0) === 0) {
        return this.buildCheckResult(
          'deposit_patterns',
          'pass',
          `${reviewAnalysis.requiredReviewedCount}/${reviewAnalysis.requiredCandidateCount} HIGH/VERY_HIGH deposit-pattern candidate${reviewAnalysis.requiredCandidateCount === 1 ? '' : 's'} reviewed or dismissed.`,
          reviewAnalysis
        );
      }

      return this.buildCheckResult(
        'deposit_patterns',
        'fail',
        `${reviewAnalysis.pendingRequiredCount} HIGH/VERY_HIGH deposit-pattern candidate${reviewAnalysis.pendingRequiredCount === 1 ? '' : 's'} still need review. Open Review Patterns, fix true positives, then dismiss or review the rest.`,
        reviewAnalysis
      );
    },

    refreshDepositPatternReview(options = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const analysis = buildCaseDepositPatternReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
      );
      this.depositPatternCandidates = analysis.candidates || [];
      this.depositPatternLastRunSummary = analysis;
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        deposit_patterns: this.buildDepositPatternReviewCheckResult(analysis)
      };
      this.persistCaseSupportState();
      if (!options.silent) {
        this.depositPatternStatus = analysis.summary;
      } else if (!this.depositPatternStatus) {
        this.depositPatternStatus = analysis.summary;
      }
      return analysis;
    },

    saveDepositPatternDecisions(candidates = [], status = CASE_DEPOSIT_PATTERN_REVIEW_STATUS_REVIEWED, options = {}) {
      const sourceCandidates = (Array.isArray(candidates) ? candidates : []).filter(candidate => candidate?.reviewKey);
      if (sourceCandidates.length === 0) return this.refreshDepositPatternReview({ silent: true });
      const existingReview = this.caseSupportState.workflowState.depositPatternReview && typeof this.caseSupportState.workflowState.depositPatternReview === 'object'
        ? this.caseSupportState.workflowState.depositPatternReview
        : {};
      const decisionsByKey = { ...(existingReview.decisionsByKey || {}) };
      const normalizedStatus = normalizeCaseDepositPatternReviewStatus(status);
      const now = new Date().toISOString();

      sourceCandidates.forEach((candidate) => {
        const comment = normalizeCell(this.depositPatternDecisionDrafts[candidate.reviewKey]);
        decisionsByKey[candidate.reviewKey] = {
          status: normalizedStatus,
          label: normalizedStatus === CASE_DEPOSIT_PATTERN_REVIEW_STATUS_DISMISSED ? 'Dismissed' : 'Reviewed',
          comment,
          basisHash: candidate.reviewBasisHash,
          grade: candidate.grade,
          parentHash: candidate.parentHash,
          childHash: candidate.childHash,
          childRowNumber: candidate.childOutputRowNumber || candidate.childRowNumber,
          reviewedAtUtc: now,
          revision: Number(this.caseSupportState.caseDataRevision) || 0,
          source: options.source || 'deposit-pattern-modal'
        };
      });

      this.caseSupportState.workflowState.depositPatternReview = {
        ...existingReview,
        decisionsByKey,
        lastTouchedUtc: now
      };
      this.depositPatternLastRunSummary = null;
      const refreshed = this.refreshDepositPatternReview({ silent: true });
      this.depositPatternStatus = `${sourceCandidates.length} deposit-pattern candidate${sourceCandidates.length === 1 ? '' : 's'} marked ${normalizedStatus === CASE_DEPOSIT_PATTERN_REVIEW_STATUS_DISMISSED ? 'dismissed' : 'reviewed'}. ${refreshed.pendingRequiredCount || 0} required candidate${(refreshed.pendingRequiredCount || 0) === 1 ? '' : 's'} remain pending.`;
      return refreshed;
    },

    markDepositPatternCandidateReviewed(candidate) {
      return this.saveDepositPatternDecisions([candidate], CASE_DEPOSIT_PATTERN_REVIEW_STATUS_REVIEWED, { source: 'mark-card-reviewed' });
    },

    dismissDepositPatternCandidate(candidate) {
      return this.saveDepositPatternDecisions([candidate], CASE_DEPOSIT_PATTERN_REVIEW_STATUS_DISMISSED, { source: 'dismiss-card' });
    },

    reopenDepositPatternCandidate(candidate) {
      if (!candidate?.reviewKey) return null;
      const existingReview = this.caseSupportState.workflowState.depositPatternReview && typeof this.caseSupportState.workflowState.depositPatternReview === 'object'
        ? this.caseSupportState.workflowState.depositPatternReview
        : {};
      const decisionsByKey = { ...(existingReview.decisionsByKey || {}) };
      delete decisionsByKey[candidate.reviewKey];
      this.caseSupportState.workflowState.depositPatternReview = {
        ...existingReview,
        decisionsByKey,
        lastTouchedUtc: new Date().toISOString()
      };
      this.depositPatternLastRunSummary = null;
      const refreshed = this.refreshDepositPatternReview({ silent: true });
      this.depositPatternStatus = `Reopened row ${candidate.childOutputRowNumber || candidate.childRowNumber} for review.`;
      return refreshed;
    },

    markVisibleDepositPatternCandidatesReviewed() {
      return this.saveDepositPatternDecisions(this.depositPatternVisibleCandidates.filter(candidate => !candidate.hasCurrentDecision), CASE_DEPOSIT_PATTERN_REVIEW_STATUS_REVIEWED, { source: 'mark-visible-reviewed' });
    },

    dismissVisibleDepositPatternCandidates() {
      return this.saveDepositPatternDecisions(this.depositPatternVisibleCandidates.filter(candidate => !candidate.hasCurrentDecision), CASE_DEPOSIT_PATTERN_REVIEW_STATUS_DISMISSED, { source: 'dismiss-visible' });
    },

    dismissRemainingDepositPatternCandidatesAndComplete() {
      const analysis = this.refreshDepositPatternReview({ silent: true });
      const pending = (analysis.candidates || []).filter(candidate => !candidate.hasCurrentDecision);
      const refreshed = pending.length > 0
        ? this.saveDepositPatternDecisions(pending, CASE_DEPOSIT_PATTERN_REVIEW_STATUS_DISMISSED, { source: 'dismiss-remaining-complete' })
        : analysis;
      return this.markDepositPatternReviewComplete(refreshed, { source: 'dismiss-remaining-complete' });
    },

    markDepositPatternReviewComplete(analysis = null, options = {}) {
      const reviewAnalysis = analysis || this.refreshDepositPatternReview({ silent: true });
      if ((reviewAnalysis.pendingRequiredCount || 0) > 0) {
        this.depositPatternStatus = `${reviewAnalysis.pendingRequiredCount} HIGH/VERY_HIGH candidate${reviewAnalysis.pendingRequiredCount === 1 ? '' : 's'} still need review. Dismiss remaining candidates or use manual workflow completion if you intentionally need to override.`;
        return false;
      }
      const existingReview = this.caseSupportState.workflowState.depositPatternReview && typeof this.caseSupportState.workflowState.depositPatternReview === 'object'
        ? this.caseSupportState.workflowState.depositPatternReview
        : {};
      this.caseSupportState.workflowState.depositPatternReview = {
        ...existingReview,
        completedAtUtc: new Date().toISOString(),
        completedBasisHash: reviewAnalysis.currentBasisHash,
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        requiredCandidateCount: reviewAnalysis.requiredCandidateCount || 0,
        requiredReviewedCount: reviewAnalysis.requiredReviewedCount || 0,
        totalCandidateCount: reviewAnalysis.totalCandidateCount || 0,
        optionalCandidateCount: reviewAnalysis.optionalCandidateCount || 0,
        completionSource: options.source || 'deposit-pattern-modal'
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_DEPOSIT_PATTERNS_REVIEWED, options.source || 'review_deposit_patterns', { silent: true });
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        deposit_patterns: this.buildDepositPatternReviewCheckResult(reviewAnalysis)
      };
      this.persistCaseSupportState();
      this.loadOutputRows(buildCaseDepositPatternAuditRows(reviewAnalysis, 'Review confirmed'), {
        title: 'Deposit pattern review audit',
        detail: 'High-priority deposit pattern review was marked complete for the current scan.',
        sourceActionId: 'review_deposit_patterns',
        target: 'audit-only'
      });
      this.depositPatternStatus = 'Deposit-pattern review marked complete for the current scan.';
      this.showCaseToast('Deposit-pattern review marked complete.');
      return true;
    },

    async openKnownEntityCasingReviewModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening known-entity casing review…',
        message: 'Scanning Column J notes against curator terms.',
        target: 'known-entity-casing-modal',
        finishMessage: 'Known-entity casing review ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.entityCuratorLocalOverlay = loadEntityCuratorOverlayFromStorage();
        this.clearCasePerformanceCache?.();
        this.knownEntityCasingSearch = '';
        this.knownEntityCasingViewFilter = options?.view || 'pending';
        this.knownEntityCasingKindFilter = options?.kind || 'all';
        this.knownEntityCasingSelectedByKey = {};
        this.knownEntityCasingStatus = 'Scanning Column J notes for known-entity casing candidates.';
        this.knownEntityCasingLastRunSummary = null;
        if (options?.forceRemote || this.entityCuratorRemote.status === 'idle') {
          await this.loadEntityCuratorRemoteKeywords({ force: !!options?.forceRemote, silent: true });
        }
        this.showKnownEntityCasingModal = true;
        await this.yieldToCaseUi();
        return this.refreshKnownEntityCasingReview({ silent: true });
      });
    },

    cancelKnownEntityCasingReviewModal() {
      if (this.knownEntityCasingRunning) return;
      this.showKnownEntityCasingModal = false;
    },

    buildKnownEntityCasingReviewCheckResult(analysis = null) {
      const reviewAnalysis = analysis || buildCaseKnownEntityCasingReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
      );

      if ((reviewAnalysis.patternsLoaded || 0) === 0) {
        return this.buildCheckResult(
          'known_entity_note_casing',
          'warn',
          'No known-entity curator terms are loaded yet. Refresh the curator lists, add local terms, or manually complete this step if review is not needed.',
          reviewAnalysis
        );
      }

      if ((reviewAnalysis.totalCandidateCount || 0) === 0) {
        return this.buildCheckResult(
          'known_entity_note_casing',
          'pass',
          'No known-entity note casing candidates were found in Column J.',
          reviewAnalysis
        );
      }

      if ((reviewAnalysis.pendingCount || 0) === 0) {
        return this.buildCheckResult(
          'known_entity_note_casing',
          'pass',
          `${reviewAnalysis.reviewedCount}/${reviewAnalysis.totalCandidateCount} known-entity casing candidate${reviewAnalysis.totalCandidateCount === 1 ? '' : 's'} reviewed, applied, or dismissed.`,
          reviewAnalysis
        );
      }

      return this.buildCheckResult(
        'known_entity_note_casing',
        'fail',
        `${reviewAnalysis.pendingCount} known-entity note casing candidate${reviewAnalysis.pendingCount === 1 ? '' : 's'} still need review. Apply true positives and dismiss false positives before moving on.`,
        reviewAnalysis
      );
    },

    refreshKnownEntityCasingReview(options = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      if (options.forceRemote) {
        this.loadEntityCuratorRemoteKeywords({ force: true, silent: true })
          .then(() => this.refreshKnownEntityCasingReview({ ...options, forceRemote: false, silent: false }))
          .catch(() => this.refreshKnownEntityCasingReview({ ...options, forceRemote: false, silent: false }));
      }
      this.entityCuratorLocalOverlay = loadEntityCuratorOverlayFromStorage();
      this.clearCasePerformanceCache?.();
      const analysis = buildCaseKnownEntityCasingReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
      );
      this.knownEntityCasingCandidates = analysis.candidates || [];
      this.knownEntityCasingLastRunSummary = analysis;
      const validKeys = new Set(this.knownEntityCasingCandidates.filter(candidate => !candidate.hasCurrentDecision).map(candidate => candidate.reviewKey));
      this.knownEntityCasingSelectedByKey = Object.fromEntries(
        Object.entries(this.knownEntityCasingSelectedByKey || {}).filter(([key, selected]) => selected && validKeys.has(key))
      );
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        known_entity_note_casing: this.buildKnownEntityCasingReviewCheckResult(analysis)
      };
      this.persistCaseSupportState();
      if (!options.silent) {
        this.knownEntityCasingStatus = analysis.summary;
      } else if (!this.knownEntityCasingStatus) {
        this.knownEntityCasingStatus = analysis.summary;
      }
      return analysis;
    },

    saveKnownEntityCasingDecisions(candidates = [], status = CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_REVIEWED, options = {}) {
      const sourceCandidates = (Array.isArray(candidates) ? candidates : []).filter(candidate => candidate?.reviewKey);
      if (sourceCandidates.length === 0) return this.refreshKnownEntityCasingReview({ silent: true });
      const existingReview = this.caseSupportState.workflowState.knownEntityCasingReview && typeof this.caseSupportState.workflowState.knownEntityCasingReview === 'object'
        ? this.caseSupportState.workflowState.knownEntityCasingReview
        : {};
      const decisionsByKey = { ...(existingReview.decisionsByKey || {}) };
      const normalizedStatus = normalizeKnownEntityCasingReviewStatus(status);
      const now = new Date().toISOString();

      sourceCandidates.forEach((candidate) => {
        decisionsByKey[candidate.reviewKey] = {
          status: normalizedStatus,
          label: formatKnownEntityCasingReviewStatus(normalizedStatus),
          basisHash: candidate.reviewBasisHash,
          rowNumber: candidate.rowNumber,
          pathLabel: candidate.pathLabel || '',
          transactionHash: candidate.transactionHash || '',
          presentedName: candidate.presentedName || '',
          matchedForms: Array.isArray(candidate.matchedForms) ? candidate.matchedForms.slice() : [],
          comment: options.comment || '',
          reviewedAtUtc: now,
          revision: Number(this.caseSupportState.caseDataRevision) || 0,
          source: options.source || 'known-entity-casing-modal'
        };
      });

      this.caseSupportState.workflowState.knownEntityCasingReview = {
        ...existingReview,
        decisionsByKey,
        lastTouchedUtc: now
      };
      this.knownEntityCasingLastRunSummary = null;
      const refreshed = this.refreshKnownEntityCasingReview({ silent: true });
      if (!options.silent) {
        this.knownEntityCasingStatus = `${sourceCandidates.length} known-entity casing candidate${sourceCandidates.length === 1 ? '' : 's'} marked ${formatKnownEntityCasingReviewStatus(normalizedStatus).toLowerCase()}. ${refreshed.pendingCount || 0} pending.`;
      }
      return refreshed;
    },

    selectVisibleKnownEntityCasingCandidates() {
      const next = { ...(this.knownEntityCasingSelectedByKey || {}) };
      this.knownEntityCasingVisibleCandidates.forEach((candidate) => {
        if (!candidate.hasCurrentDecision) next[candidate.reviewKey] = true;
      });
      this.knownEntityCasingSelectedByKey = next;
      this.knownEntityCasingStatus = `${this.knownEntityCasingSelectedCount} visible pending candidate${this.knownEntityCasingSelectedCount === 1 ? '' : 's'} selected.`;
    },

    clearKnownEntityCasingSelections() {
      this.knownEntityCasingSelectedByKey = {};
      this.knownEntityCasingStatus = 'Known-entity casing selections cleared.';
    },

    applyKnownEntityCasingCandidate(candidate) {
      return this.applyKnownEntityCasingFixes([candidate]);
    },

    applySelectedKnownEntityCasingFixes() {
      return this.applyKnownEntityCasingFixes(this.knownEntityCasingSelectedCandidates);
    },

    applyKnownEntityCasingFixes(candidates = [], options = {}) {
      if (this.knownEntityCasingRunning) return null;
      const selected = (Array.isArray(candidates) ? candidates : []).filter(candidate => candidate?.reviewKey && !candidate.hasCurrentDecision);
      if (selected.length === 0) {
        this.knownEntityCasingStatus = 'Select at least one pending casing candidate to apply.';
        return null;
      }

      const confirmed = options.skipConfirm === true || window.confirm(`Apply ${selected.length} known-entity casing fix${selected.length === 1 ? '' : 'es'} to Main Data Column J?`);
      if (!confirmed) return null;

      this.knownEntityCasingRunning = true;
      try {
        const mainSheet = this.findLogicalSheetMeta('main');
        if (!mainSheet) throw new Error('Main Data sheet was not found.');
        const state = this.getSheetStateById(mainSheet.id);
        const beforeAnalysis = this.knownEntityCasingAnalysis;
        const applyResult = applyKnownEntityCasingFixesToMainMatrix(state.data, selected);

        if (applyResult.changed) {
          this.writeSheetStateById(mainSheet.id, {
            ...state,
            data: applyResult.data,
            frozenTop: 1
          });
          this.markCaseDataMutated('known_entity_note_casing_fixes_applied', {
            rowsUpdated: applyResult.rowsUpdated,
            fixesApplied: applyResult.fixCount,
            skipped: applyResult.skippedCount
          });
          this.recordWorkflowToolRun('known_entity_note_casing_apply_fixes', {
            rowsUpdated: applyResult.rowsUpdated,
            fixesApplied: applyResult.fixCount,
            skipped: applyResult.skippedCount
          });
        }

        this.saveKnownEntityCasingDecisions(selected, CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_APPLIED, { source: 'apply-fix', silent: true });
        this.knownEntityCasingSelectedByKey = {};
        const refreshed = this.refreshKnownEntityCasingReview({ silent: true });
        this.loadOutputRows(buildKnownEntityCasingAuditRows(beforeAnalysis, `Applied ${applyResult.fixCount} known-entity casing fix${applyResult.fixCount === 1 ? '' : 'es'}`), {
          title: 'Known entity note casing audit',
          detail: 'Audit only. Selected Column J casing fixes were applied directly to Main Data.',
          sourceActionId: 'review_known_entity_note_casing',
          target: 'audit-only'
        });
        this.knownEntityCasingStatus = applyResult.changed
          ? `Applied ${applyResult.fixCount} casing fix${applyResult.fixCount === 1 ? '' : 'es'} across ${applyResult.rowsUpdated} Main Data row${applyResult.rowsUpdated === 1 ? '' : 's'}. ${refreshed.pendingCount || 0} pending.`
          : `No rows changed. ${applyResult.skippedCount} stale or unchanged candidate${applyResult.skippedCount === 1 ? '' : 's'} skipped.`;
        this.showCaseToast(this.knownEntityCasingStatus);
        return refreshed;
      } catch (error) {
        this.knownEntityCasingStatus = error?.message || 'Known-entity casing fix failed.';
        return null;
      } finally {
        this.knownEntityCasingRunning = false;
      }
    },

    dismissKnownEntityCasingCandidate(candidate) {
      return this.saveKnownEntityCasingDecisions([candidate], CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_DISMISSED, { source: 'dismiss-card' });
    },

    dismissSelectedKnownEntityCasingCandidates() {
      return this.saveKnownEntityCasingDecisions(this.knownEntityCasingSelectedCandidates, CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_DISMISSED, { source: 'dismiss-selected' });
    },

    dismissRemainingKnownEntityCasingCandidatesAndComplete() {
      const analysis = this.refreshKnownEntityCasingReview({ silent: true });
      const pending = (analysis.candidates || []).filter(candidate => !candidate.hasCurrentDecision);
      const refreshed = pending.length > 0
        ? this.saveKnownEntityCasingDecisions(pending, CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_DISMISSED, { source: 'dismiss-remaining-complete' })
        : analysis;
      this.knownEntityCasingSelectedByKey = {};
      return this.markKnownEntityCasingReviewComplete(refreshed, { source: 'dismiss-remaining-complete' });
    },

    reopenKnownEntityCasingCandidate(candidate) {
      if (!candidate?.reviewKey) return null;
      const existingReview = this.caseSupportState.workflowState.knownEntityCasingReview && typeof this.caseSupportState.workflowState.knownEntityCasingReview === 'object'
        ? this.caseSupportState.workflowState.knownEntityCasingReview
        : {};
      const decisionsByKey = { ...(existingReview.decisionsByKey || {}) };
      delete decisionsByKey[candidate.reviewKey];
      this.caseSupportState.workflowState.knownEntityCasingReview = {
        ...existingReview,
        decisionsByKey,
        lastTouchedUtc: new Date().toISOString()
      };
      this.knownEntityCasingLastRunSummary = null;
      const refreshed = this.refreshKnownEntityCasingReview({ silent: true });
      this.knownEntityCasingStatus = `Reopened known-entity casing candidate for row ${candidate.rowNumber}.`;
      return refreshed;
    },

    markKnownEntityCasingReviewComplete(analysis = null, options = {}) {
      const reviewAnalysis = analysis || this.refreshKnownEntityCasingReview({ silent: true });
      if ((reviewAnalysis.pendingCount || 0) > 0) {
        this.knownEntityCasingStatus = `${reviewAnalysis.pendingCount} known-entity casing candidate${reviewAnalysis.pendingCount === 1 ? '' : 's'} still need review. Apply true positives, dismiss false positives, or manually complete the workflow step if you intentionally need to override.`;
        return false;
      }
      const existingReview = this.caseSupportState.workflowState.knownEntityCasingReview && typeof this.caseSupportState.workflowState.knownEntityCasingReview === 'object'
        ? this.caseSupportState.workflowState.knownEntityCasingReview
        : {};
      this.caseSupportState.workflowState.knownEntityCasingReview = {
        ...existingReview,
        completedAtUtc: new Date().toISOString(),
        completedBasisHash: reviewAnalysis.currentBasisHash,
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        totalCandidateCount: reviewAnalysis.totalCandidateCount || 0,
        reviewedCount: reviewAnalysis.reviewedCount || 0,
        pendingCount: reviewAnalysis.pendingCount || 0,
        completionSource: options.source || 'known-entity-casing-modal'
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_KNOWN_ENTITY_CASING_REVIEWED, options.source || 'review_known_entity_note_casing', { silent: true });
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        known_entity_note_casing: this.buildKnownEntityCasingReviewCheckResult(reviewAnalysis)
      };
      this.persistCaseSupportState();
      this.loadOutputRows(buildKnownEntityCasingAuditRows(reviewAnalysis, 'Review confirmed'), {
        title: 'Known entity note casing audit',
        detail: 'Known-entity note casing review was marked complete for the current scan.',
        sourceActionId: 'review_known_entity_note_casing',
        target: 'audit-only'
      });
      this.knownEntityCasingStatus = 'Known-entity note casing review marked complete for the current scan.';
      this.showCaseToast(this.knownEntityCasingStatus);
      return true;
    },

    selectKnownEntityCasingCandidateRow(candidate) {
      if (!candidate || !Number.isInteger(candidate.rowIndex)) return false;
      this.activateLogicalSheet('main');
      this.$nextTick(() => {
        const sheet = this.getInputSheet?.();
        sheet?.selectCell?.(candidate.rowIndex, 9);
        sheet?.hotInstance?.scrollViewportTo?.(candidate.rowIndex, 9, true, true);
      });
      this.knownEntityCasingStatus = `Selected Main Data row ${candidate.rowNumber}, Column J.`;
      return true;
    },

    async copyKnownEntityCasingCandidateSearch(candidate) {
      const text = [
        `Expected: ${candidate?.presentedName || ''}`,
        `Matched: ${(candidate?.matchedForms || []).join(', ')}`,
        `Row: ${candidate?.rowNumber || ''}`,
        `Path: ${candidate?.pathLabel || ''}`,
        `Hash: ${candidate?.transactionHash || ''}`,
        '',
        candidate?.currentNotes || '',
        '',
        'Proposed:',
        candidate?.proposedNotes || ''
      ].join('\n');
      const copied = await this.copyTextToClipboard(text, 'Known-entity casing search text copied.');
      this.knownEntityCasingStatus = copied ? 'Known-entity casing search text copied.' : 'Could not copy search text in this browser.';
      return copied;
    },

    async openDuplicateHashLabelerModal() {
      return this.runCaseModalOperation({
        title: 'Opening duplicate-hash labeler…',
        message: 'Scanning Main Data for duplicate hashes with differing path labels.',
        target: 'duplicate-hash-labeler-modal',
        finishMessage: 'Duplicate-hash labeler ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.duplicateHashLabelerSearch = '';
        this.duplicateHashLabelerViewFilter = 'eligible';
        this.duplicateHashLabelerSelectedByKey = {};
        this.duplicateHashLabelerStatus = 'Scanning Main Data for duplicate transaction hashes with differing path labels.';
        this.showDuplicateHashLabelerModal = true;
        await this.yieldToCaseUi();
        this.refreshDuplicateHashLabeler({ silent: true });
        return true;
      });
    },

    cancelDuplicateHashLabelerModal() {
      if (this.duplicateHashLabelerRunning) return;
      this.showDuplicateHashLabelerModal = false;
    },

    refreshDuplicateHashLabeler(options = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const analysis = buildCaseDuplicateHashLabelerAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
      );
      this.duplicateHashLabelerItems = analysis.candidates || [];
      this.duplicateHashLabelerLastRunSummary = analysis;
      const validKeys = new Set(this.duplicateHashLabelerItems.filter(item => item.isEligible).map(item => item.reviewKey));
      this.duplicateHashLabelerSelectedByKey = Object.fromEntries(
        Object.entries(this.duplicateHashLabelerSelectedByKey || {}).filter(([key, selected]) => selected && validKeys.has(key))
      );
      if (!options.silent) {
        this.duplicateHashLabelerStatus = analysis.summary;
      } else if (!this.duplicateHashLabelerStatus) {
        this.duplicateHashLabelerStatus = analysis.summary;
      }
      return analysis;
    },

    buildDuplicateHashLabelerCheckResult(analysis = null) {
      const reviewAnalysis = analysis || buildCaseDuplicateHashLabelerAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
      );
      if ((reviewAnalysis.blockedProposalCount || 0) > 0) {
        return this.buildCheckResult(
          'duplicate_hashes',
          'fail',
          `${reviewAnalysis.blockedProposalCount} duplicate-hash note proposal${reviewAnalysis.blockedProposalCount === 1 ? '' : 's'} need manual review because they would change non-duplicate Column J notes.`,
          reviewAnalysis
        );
      }
      if ((reviewAnalysis.eligibleEntryCount || 0) > 0) {
        return this.buildCheckResult(
          'duplicate_hashes',
          'fail',
          `${reviewAnalysis.eligibleEntryCount} duplicate-hash note update${reviewAnalysis.eligibleEntryCount === 1 ? '' : 's'} remain. Open Label Duplicates to refresh Column J notes and clear stale duplicate labels.`,
          reviewAnalysis
        );
      }
      return this.buildCheckResult(
        'duplicate_hashes',
        'pass',
        reviewAnalysis.duplicateHashGroupCount > 0
          ? `${reviewAnalysis.duplicateHashGroupCount} duplicate transaction hash group${reviewAnalysis.duplicateHashGroupCount === 1 ? '' : 's'} are documented and the live Duplicate Hash Table is available.`
          : 'No duplicate transaction hashes with differing path labels are currently detected.',
        reviewAnalysis
      );
    },

    selectVisibleDuplicateHashLabelerItems() {
      const next = { ...(this.duplicateHashLabelerSelectedByKey || {}) };
      this.duplicateHashLabelerVisibleItems.forEach((item) => {
        if (item.isEligible) next[item.reviewKey] = true;
      });
      this.duplicateHashLabelerSelectedByKey = next;
      this.duplicateHashLabelerStatus = `${this.duplicateHashLabelerSelectedCount} eligible visible update${this.duplicateHashLabelerSelectedCount === 1 ? '' : 's'} selected.`;
    },

    clearDuplicateHashLabelerSelections() {
      this.duplicateHashLabelerSelectedByKey = {};
      this.duplicateHashLabelerStatus = 'Duplicate-hash selections cleared.';
    },

    applySelectedDuplicateHashLabelerUpdates() {
      return this.applyDuplicateHashLabelerUpdates(this.duplicateHashLabelerSelectedItems, { source: 'apply-selected' });
    },

    applyAllEligibleDuplicateHashUpdates(options = {}) {
      const analysis = this.refreshDuplicateHashLabeler({ silent: true });
      return this.applyDuplicateHashLabelerUpdates(analysis.eligibleItems || [], { source: options.source || 'apply-all', skipConfirm: !!options.skipConfirm });
    },

    applyDuplicateHashLabelerItem(item) {
      return this.applyDuplicateHashLabelerUpdates([item], { source: 'apply-card' });
    },

    applyDuplicateHashLabelerUpdates(items = [], options = {}) {
      if (this.duplicateHashLabelerRunning) return null;
      const selected = (Array.isArray(items) ? items : []).filter(item => item?.reviewKey && item.isEligible);
      if (selected.length === 0) {
        this.duplicateHashLabelerStatus = 'No eligible duplicate-hash note updates are selected.';
        return null;
      }
      const confirmed = options.skipConfirm === true || window.confirm(`Apply ${selected.length} duplicate-hash note update${selected.length === 1 ? '' : 's'} to Main Data Column J? This refreshes stale duplicate annotations instead of adding duplicate wording again.`);
      if (!confirmed) return null;

      this.duplicateHashLabelerRunning = true;
      try {
        const mainSheet = this.findLogicalSheetMeta('main');
        if (!mainSheet) throw new Error('Main Data sheet was not found.');
        const state = this.getSheetStateById(mainSheet.id);
        const applyResult = applyDuplicateHashLabelerItemsToMainMatrix(state.data, selected);

        if (applyResult.changed) {
          this.writeSheetStateById(mainSheet.id, {
            ...state,
            data: applyResult.data,
            frozenTop: 1
          });
          this.markCaseDataMutated('duplicate_hash_notes_updated', {
            rowsUpdated: applyResult.rowsUpdated,
            duplicateNotesUpdated: applyResult.duplicateNotesUpdated,
            staleNotesCleared: applyResult.staleNotesCleared,
            skipped: applyResult.skippedCount
          });
          this.recordWorkflowToolRun('duplicate_hash_labeler_apply_updates', {
            rowsUpdated: applyResult.rowsUpdated,
            duplicateNotesUpdated: applyResult.duplicateNotesUpdated,
            staleNotesCleared: applyResult.staleNotesCleared,
            skipped: applyResult.skippedCount,
            source: options.source || 'duplicate-hash-labeler'
          });
        }

        this.duplicateHashLabelerSelectedByKey = {};
        this.duplicateHashLabelerLastRunSummary = null;
        const refreshed = this.refreshDuplicateHashLabeler({ silent: true });
        if ((refreshed.eligibleEntryCount || 0) === 0) {
          this.caseSupportState.workflowState.duplicateHashLabelerReview = {
            completedAtUtc: new Date().toISOString(),
            completedBasisHash: refreshed.currentBasisHash,
            revision: Number(this.caseSupportState.caseDataRevision) || 0,
            duplicateHashGroupCount: refreshed.duplicateHashGroupCount || 0,
            duplicateEntryCount: refreshed.duplicateEntryCount || 0,
            eligibleEntryCount: refreshed.eligibleEntryCount || 0,
            completionSource: options.source || 'duplicate-hash-labeler-apply'
          };
          this.recordWorkflowSignal(WORKFLOW_SIGNAL_DUPLICATE_HASHES_LABELED, 'label_duplicate_hashes', { silent: true });
        }
        this.caseSupportState.checkResultsById = {
          ...(this.caseSupportState.checkResultsById || {}),
          duplicate_hashes: this.buildDuplicateHashLabelerCheckResult(refreshed)
        };
        this.persistCaseSupportState();
        this.loadOutputRows(buildDuplicateHashLabelerAuditRows(refreshed, `Applied ${applyResult.rowsUpdated} duplicate-hash note update${applyResult.rowsUpdated === 1 ? '' : 's'}`), {
          title: 'Duplicate hash labeler audit',
          detail: 'Audit only. Selected Column J duplicate-hash notes were applied directly to Main Data.',
          sourceActionId: 'label_duplicate_hashes',
          target: 'audit-only'
        });
        const safetySkippedText = applyResult.safetySkippedCount
          ? ` ${applyResult.safetySkippedCount} unsafe proposal${applyResult.safetySkippedCount === 1 ? '' : 's'} skipped to protect non-duplicate Column J notes.`
          : '';
        this.duplicateHashLabelerStatus = applyResult.changed
          ? `Updated ${applyResult.rowsUpdated} row${applyResult.rowsUpdated === 1 ? '' : 's'}: ${applyResult.duplicateNotesUpdated} duplicate annotation${applyResult.duplicateNotesUpdated === 1 ? '' : 's'} refreshed and ${applyResult.staleNotesCleared} stale note${applyResult.staleNotesCleared === 1 ? '' : 's'} cleared. ${refreshed.eligibleEntryCount || 0} update${(refreshed.eligibleEntryCount || 0) === 1 ? '' : 's'} remain.${safetySkippedText}`
          : `No rows changed. ${applyResult.skippedCount} stale, unsafe, or unchanged update${applyResult.skippedCount === 1 ? '' : 's'} skipped.${safetySkippedText}`;
        this.showCaseToast(this.duplicateHashLabelerStatus);
        return refreshed;
      } catch (error) {
        this.duplicateHashLabelerStatus = error?.message || 'Duplicate hash labeler failed.';
        return null;
      } finally {
        this.duplicateHashLabelerRunning = false;
      }
    },

    markDuplicateHashLabelerReviewComplete(analysis = null, options = {}) {
      const reviewAnalysis = analysis || this.refreshDuplicateHashLabeler({ silent: true });
      if ((reviewAnalysis.blockedProposalCount || 0) > 0) {
        this.duplicateHashLabelerStatus = `${reviewAnalysis.blockedProposalCount} duplicate-hash note proposal${reviewAnalysis.blockedProposalCount === 1 ? '' : 's'} need manual review because they would change non-duplicate Column J notes. The labeler will not apply those automatically.`;
        return false;
      }
      if ((reviewAnalysis.eligibleEntryCount || 0) > 0) {
        this.duplicateHashLabelerStatus = `${reviewAnalysis.eligibleEntryCount} duplicate-hash note update${reviewAnalysis.eligibleEntryCount === 1 ? '' : 's'} still need to be applied or cleared. Apply the updates, or manually complete the workflow step if you intentionally need to override.`;
        return false;
      }
      this.caseSupportState.workflowState.duplicateHashLabelerReview = {
        completedAtUtc: new Date().toISOString(),
        completedBasisHash: reviewAnalysis.currentBasisHash,
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        duplicateHashGroupCount: reviewAnalysis.duplicateHashGroupCount || 0,
        duplicateEntryCount: reviewAnalysis.duplicateEntryCount || 0,
        eligibleEntryCount: reviewAnalysis.eligibleEntryCount || 0,
        completionSource: options.source || 'duplicate-hash-labeler-modal'
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_DUPLICATE_HASHES_LABELED, options.source || 'label_duplicate_hashes', { silent: true });
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        duplicate_hashes: this.buildDuplicateHashLabelerCheckResult(reviewAnalysis)
      };
      this.persistCaseSupportState();
      this.loadOutputRows(buildDuplicateHashLabelerAuditRows(reviewAnalysis, 'Review confirmed'), {
        title: 'Duplicate hash labeler audit',
        detail: 'Duplicate hash labeling was marked complete for the current scan. The duplicate hash table remains live from Main Data.',
        sourceActionId: 'label_duplicate_hashes',
        target: 'audit-only'
      });
      this.duplicateHashLabelerStatus = 'Duplicate transaction hash labeling marked complete for the current scan.';
      this.showCaseToast(this.duplicateHashLabelerStatus);
      return true;
    },

    buildDuplicateHashTablePreview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging duplicate hash table…',
          message: 'Building duplicate hash table rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Duplicate hash table staged.'
        }, () => this.buildDuplicateHashTablePreview({ ...options, __caseOperationWrapped: true }));
      }
      const analysis = this.refreshDuplicateHashLabeler({ silent: true });
      const rows = buildDuplicateHashTableRows(analysis);
      this.loadOutputRows(rows, {
        title: 'Duplicate Hash Table preview',
        detail: 'Live duplicate hash table generated from current Main Data. This table can be included in the report package.',
        sourceDeliverableId: 'duplicate_hash_table',
        target: 'deliverable-preview'
      });
      const deliverable = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(item => item.id === 'duplicate_hash_table');
      if (deliverable) this.markDeliverableBuilt(deliverable);
      this.duplicateHashLabelerStatus = `${Math.max(0, rows.length - 1)} duplicate hash table row${rows.length === 2 ? '' : 's'} staged in Output.`;
      this.showCaseToast(this.duplicateHashLabelerStatus);
      return rows;
    },

    selectDuplicateHashLabelerRow(item) {
      if (!item || !Number.isInteger(item.rowIndex)) return false;
      this.activateLogicalSheet('main');
      this.$nextTick(() => {
        const sheet = this.getInputSheet?.();
        sheet?.selectCell?.(item.rowIndex, 9);
        sheet?.hotInstance?.scrollViewportTo?.(item.rowIndex, 9, true, true);
      });
      this.duplicateHashLabelerStatus = `Selected Main Data row ${item.rowNumber}, Column J.`;
      return true;
    },

    async copyDuplicateHashLabelerSearch(item) {
      const text = [
        `Row: ${item?.rowNumber || ''}`,
        `Path: ${item?.pathLabel || ''}`,
        `Hash: ${item?.transactionHash || ''}`,
        `Other labels: ${(item?.otherPathLabels || []).join(', ')}`,
        `Status: ${item?.statusLabel || ''}`,
        '',
        'Current notes:',
        item?.currentNotes || '',
        '',
        'Proposed notes:',
        item?.proposedNotes || ''
      ].join('\n');
      const copied = await this.copyTextToClipboard(text, 'Duplicate-hash review text copied.');
      this.duplicateHashLabelerStatus = copied ? 'Duplicate-hash review text copied.' : 'Could not copy duplicate-hash text in this browser.';
      return copied;
    },

    async openNoteHashRewriteModal() {
      return this.runCaseModalOperation({
        title: 'Opening note hash rewrite…',
        message: 'Scanning notes for hash references that can be replaced with path labels.',
        target: 'note-hash-rewrite-modal',
        finishMessage: 'Note hash rewrite review ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.noteHashRewriteSearch = '';
        this.noteHashRewriteViewFilter = 'eligible';
        this.noteHashRewriteSelectedByKey = {};
        this.noteHashRewriteStatus = 'Scanning Main Data Column J for Continues/Branches note sections that can be refreshed from hashes to path labels.';
        this.showNoteHashRewriteModal = true;
        await this.yieldToCaseUi();
        this.refreshNoteHashRewrite({ silent: true });
        return true;
      });
    },

    cancelNoteHashRewriteModal() {
      if (this.noteHashRewriteRunning) return;
      this.showNoteHashRewriteModal = false;
    },

    refreshNoteHashRewrite(options = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const analysis = buildCaseNoteHashRewriteAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        {
          caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
          includeUnstructured: this.noteHashRewriteIncludeUnstructured
        }
      );
      this.noteHashRewriteItems = analysis.candidates || [];
      this.noteHashRewriteLastRunSummary = analysis;
      const validKeys = new Set(this.noteHashRewriteItems.filter(item => item.isEligible).map(item => item.reviewKey));
      this.noteHashRewriteSelectedByKey = Object.fromEntries(
        Object.entries(this.noteHashRewriteSelectedByKey || {}).filter(([key, selected]) => selected && validKeys.has(key))
      );
      if (!options.silent) {
        this.noteHashRewriteStatus = analysis.summary;
      } else if (!this.noteHashRewriteStatus) {
        this.noteHashRewriteStatus = analysis.summary;
      }
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        notes_backup: this.buildNoteHashRewriteCheckResult(analysis)
      };
      this.persistCaseSupportState();
      return analysis;
    },

    buildNoteHashRewriteCheckResult(analysis = null) {
      const reviewAnalysis = analysis || buildCaseNoteHashRewriteAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        {
          caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
          includeUnstructured: this.noteHashRewriteIncludeUnstructured
        }
      );
      if (!reviewAnalysis.backupExists && (reviewAnalysis.eligibleCount || 0) > 0) {
        return this.buildCheckResult(
          'notes_backup',
          'fail',
          `${reviewAnalysis.eligibleCount} hash-to-path-label note rewrite${reviewAnalysis.eligibleCount === 1 ? '' : 's'} are ready, but an Original Notes Backup must be created or merged before applying them.`,
          reviewAnalysis
        );
      }
      if ((reviewAnalysis.eligibleCount || 0) > 0) {
        return this.buildCheckResult(
          'notes_backup',
          'fail',
          `${reviewAnalysis.eligibleCount} hash-to-path-label note rewrite${reviewAnalysis.eligibleCount === 1 ? '' : 's'} remain. Open Rewrite Notes to refresh Column J from current Column Q hashes and Column A path labels.`,
          reviewAnalysis
        );
      }
      if (!reviewAnalysis.backupExists) {
        return this.buildCheckResult(
          'notes_backup',
          'warn',
          'No Original Notes Backup exists yet. Create one before final note rewrites, or manually complete if this case has no hash references to replace.',
          reviewAnalysis
        );
      }
      return this.buildCheckResult(
        'notes_backup',
        'pass',
        `Original Notes Backup is available (${reviewAnalysis.backupRowCount} row${reviewAnalysis.backupRowCount === 1 ? '' : 's'}) and no current hash-to-path-label note rewrites remain.`,
        reviewAnalysis
      );
    },

    selectVisibleNoteHashRewriteItems() {
      const next = { ...(this.noteHashRewriteSelectedByKey || {}) };
      this.noteHashRewriteVisibleItems.forEach((item) => {
        if (item.isEligible) next[item.reviewKey] = true;
      });
      this.noteHashRewriteSelectedByKey = next;
      this.noteHashRewriteStatus = `${this.noteHashRewriteSelectedCount} eligible visible rewrite${this.noteHashRewriteSelectedCount === 1 ? '' : 's'} selected.`;
    },

    selectAllEligibleNoteHashRewriteItems() {
      const next = { ...(this.noteHashRewriteSelectedByKey || {}) };
      (this.noteHashRewriteItems || []).forEach((item) => {
        if (item.isEligible) next[item.reviewKey] = true;
      });
      this.noteHashRewriteSelectedByKey = next;
      this.noteHashRewriteViewFilter = this.noteHashRewriteSelectedCount > 0 ? 'selected' : this.noteHashRewriteViewFilter;
      this.noteHashRewriteStatus = `${this.noteHashRewriteSelectedCount} eligible note rewrite${this.noteHashRewriteSelectedCount === 1 ? '' : 's'} selected.`;
    },

    clearNoteHashRewriteSelections() {
      this.noteHashRewriteSelectedByKey = {};
      this.noteHashRewriteStatus = 'Note rewrite selections cleared.';
    },

    createOrMergeOriginalNotesBackup(options = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const replace = !!options.replace;
      const existing = this.caseSupportState.workflowState.noteHashPathLabelRewriteReview?.backup || null;
      if (replace && existing?.rows?.length) {
        const confirmed = window.confirm('Replace the existing Original Notes Backup with the current Main Data Column J notes? This removes the older backup snapshot from local case support state.');
        if (!confirmed) return null;
      }
      const nextBackup = buildCaseNoteHashRewriteBackupRecord(this.getMatrixForLogicalSheet('main'), {
        caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
        revision: Number(this.caseSupportState.caseDataRevision) || 0
      });
      const merged = replace ? nextBackup : mergeCaseNoteHashRewriteBackupRecords(existing, nextBackup);
      this.caseSupportState.workflowState.noteHashPathLabelRewriteReview = {
        ...(this.caseSupportState.workflowState.noteHashPathLabelRewriteReview || {}),
        backup: merged
      };
      this.persistCaseSupportState();
      this.noteHashRewriteLastRunSummary = null;
      const refreshed = this.refreshNoteHashRewrite({ silent: true });
      this.stageOriginalNotesBackupPreview({ silent: true });
      this.noteHashRewriteStatus = `${replace ? 'Replaced' : 'Created/merged'} Original Notes Backup with ${merged.rowCount || 0} row${(merged.rowCount || 0) === 1 ? '' : 's'} (${merged.nonBlankNotesCount || 0} nonblank notes).`;
      this.showCaseToast(this.noteHashRewriteStatus);
      return refreshed;
    },

    async applySelectedNoteHashRewrites() {
      return this.applyNoteHashRewriteUpdates(this.noteHashRewriteSelectedItems, { source: 'apply-selected' });
    },

    async applyAllEligibleNoteHashRewrites(options = {}) {
      const analysis = this.refreshNoteHashRewrite({ silent: true });
      return this.applyNoteHashRewriteUpdates(analysis.eligibleItems || [], { source: options.source || 'apply-all', skipConfirm: !!options.skipConfirm });
    },

    async applyNoteHashRewriteItem(item) {
      return this.applyNoteHashRewriteUpdates([item], { source: 'apply-card' });
    },

    async applyNoteHashRewriteUpdates(items = [], options = {}) {
      if (this.noteHashRewriteRunning) return null;
      const selected = (Array.isArray(items) ? items : []).filter(item => item?.reviewKey && item.isEligible);
      if (selected.length === 0) {
        this.noteHashRewriteStatus = 'No eligible hash-to-path-label note rewrites are selected.';
        return null;
      }
      let currentAnalysis = this.refreshNoteHashRewrite({ silent: true });
      if (!currentAnalysis.backupExists) {
        this.createOrMergeOriginalNotesBackup({ replace: false });
        currentAnalysis = this.refreshNoteHashRewrite({ silent: true });
      }
      const confirmed = options.skipConfirm === true || window.confirm(`Apply ${selected.length} hash-to-path-label note rewrite${selected.length === 1 ? '' : 's'} to Main Data Column J? A local Original Notes Backup exists and this can be re-run later if path labels change.`);
      if (!confirmed) return null;

      this.noteHashRewriteRunning = true;
      this.noteHashRewriteBatchProgress = {
        total: selected.length,
        processed: 0,
        message: selected.length > 1 ? 'Preparing batch rewrite…' : 'Preparing rewrite…'
      };
      await sleep(20);
      try {
        const mainSheet = this.findLogicalSheetMeta('main');
        if (!mainSheet) throw new Error('Main Data sheet was not found.');
        const state = this.getSheetStateById(mainSheet.id);
        const chunkSize = selected.length >= 250 ? 40 : selected.length >= 80 ? 30 : 20;
        let workingData = state.data;
        const applyResult = {
          data: workingData,
          changed: false,
          rowsUpdated: 0,
          replacementsApplied: 0,
          skippedCount: 0,
          updatedRows: []
        };

        for (let start = 0; start < selected.length; start += chunkSize) {
          const chunk = selected.slice(start, start + chunkSize);
          this.noteHashRewriteBatchProgress = {
            total: selected.length,
            processed: start,
            message: `Applying rewrite batch ${Math.floor(start / chunkSize) + 1} of ${Math.ceil(selected.length / chunkSize)}…`
          };
          this.noteHashRewriteStatus = `Applying note rewrites ${start} / ${selected.length}…`;
          await sleep(16);
          const chunkResult = applyCaseNoteHashRewriteItemsToMainMatrix(workingData, chunk);
          workingData = chunkResult.data;
          applyResult.data = workingData;
          applyResult.changed = applyResult.changed || chunkResult.changed;
          applyResult.rowsUpdated += chunkResult.rowsUpdated || 0;
          applyResult.replacementsApplied += chunkResult.replacementsApplied || 0;
          applyResult.skippedCount += chunkResult.skippedCount || 0;
          if (Array.isArray(chunkResult.updatedRows)) applyResult.updatedRows.push(...chunkResult.updatedRows);
          this.noteHashRewriteBatchProgress = {
            total: selected.length,
            processed: Math.min(selected.length, start + chunk.length),
            message: `Applied ${Math.min(selected.length, start + chunk.length)} of ${selected.length} rewrite${selected.length === 1 ? '' : 's'}…`
          };
          await sleep(10);
        }

        if (applyResult.changed) {
          this.writeSheetStateById(mainSheet.id, {
            ...state,
            data: applyResult.data,
            frozenTop: 1
          });
          this.markCaseDataMutated('note_hashes_rewritten_to_path_labels', {
            rowsUpdated: applyResult.rowsUpdated,
            replacementsApplied: applyResult.replacementsApplied,
            skipped: applyResult.skippedCount
          });
          this.recordWorkflowToolRun('note_hash_to_path_label_rewrite_apply', {
            rowsUpdated: applyResult.rowsUpdated,
            replacementsApplied: applyResult.replacementsApplied,
            skipped: applyResult.skippedCount,
            source: options.source || 'note-hash-rewrite'
          });
        }
        this.noteHashRewriteSelectedByKey = {};
        this.noteHashRewriteLastRunSummary = null;
        const refreshed = this.refreshNoteHashRewrite({ silent: true });
        if (refreshed.pass) {
          this.caseSupportState.workflowState.noteHashPathLabelRewriteReview = {
            ...(this.caseSupportState.workflowState.noteHashPathLabelRewriteReview || {}),
            completedAtUtc: new Date().toISOString(),
            completedBasisHash: refreshed.currentBasisHash,
            revision: Number(this.caseSupportState.caseDataRevision) || 0,
            eligibleCount: refreshed.eligibleCount || 0,
            backupRowCount: refreshed.backupRowCount || 0,
            completionSource: options.source || 'note-hash-rewrite-apply'
          };
          this.recordWorkflowSignal(WORKFLOW_SIGNAL_NOTE_HASH_PATH_LABEL_REWRITE_COMPLETE, 'backup_notes_replace_hashes', { silent: true });
        }
        this.caseSupportState.checkResultsById = {
          ...(this.caseSupportState.checkResultsById || {}),
          notes_backup: this.buildNoteHashRewriteCheckResult(refreshed)
        };
        this.persistCaseSupportState();
        this.loadOutputRows(buildCaseNoteHashRewriteAuditRows(refreshed, `Applied ${applyResult.rowsUpdated} note rewrite${applyResult.rowsUpdated === 1 ? '' : 's'}`), {
          title: 'Note hash-to-path-label rewrite audit',
          detail: 'Audit only. Selected Column J notes were updated directly in Main Data; the Original Notes Backup is included below.',
          sourceActionId: 'backup_notes_replace_hashes',
          target: 'audit-only'
        });
        this.noteHashRewriteStatus = applyResult.changed
          ? `Updated ${applyResult.rowsUpdated} row${applyResult.rowsUpdated === 1 ? '' : 's'} with ${applyResult.replacementsApplied} replacement${applyResult.replacementsApplied === 1 ? '' : 's'}. ${refreshed.eligibleCount || 0} rewrite${(refreshed.eligibleCount || 0) === 1 ? '' : 's'} remain.`
          : `No rows changed. ${applyResult.skippedCount} stale or unchanged rewrite${applyResult.skippedCount === 1 ? '' : 's'} skipped.`;
        this.showCaseToast(this.noteHashRewriteStatus);
        return refreshed;
      } catch (error) {
        this.noteHashRewriteStatus = error?.message || 'Note hash-to-path-label rewrite failed.';
        return null;
      } finally {
        this.noteHashRewriteBatchProgress = null;
        this.noteHashRewriteRunning = false;
      }
    },

    markNoteHashRewriteReviewComplete(analysis = null, options = {}) {
      const reviewAnalysis = analysis || this.refreshNoteHashRewrite({ silent: true });
      if (!reviewAnalysis.backupExists) {
        this.noteHashRewriteStatus = 'Create or merge an Original Notes Backup before marking this rewrite step complete. Use manual workflow completion if you intentionally need to override.';
        return false;
      }
      if ((reviewAnalysis.eligibleCount || 0) > 0) {
        this.noteHashRewriteStatus = `${reviewAnalysis.eligibleCount} hash-to-path-label note rewrite${reviewAnalysis.eligibleCount === 1 ? '' : 's'} still need to be applied. Apply them, or manually complete the workflow step if you intentionally need to override.`;
        return false;
      }
      this.caseSupportState.workflowState.noteHashPathLabelRewriteReview = {
        ...(this.caseSupportState.workflowState.noteHashPathLabelRewriteReview || {}),
        completedAtUtc: new Date().toISOString(),
        completedBasisHash: reviewAnalysis.currentBasisHash,
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        eligibleCount: reviewAnalysis.eligibleCount || 0,
        backupRowCount: reviewAnalysis.backupRowCount || 0,
        completionSource: options.source || 'note-hash-rewrite-modal'
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_NOTE_HASH_PATH_LABEL_REWRITE_COMPLETE, options.source || 'backup_notes_replace_hashes', { silent: true });
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        notes_backup: this.buildNoteHashRewriteCheckResult(reviewAnalysis)
      };
      this.persistCaseSupportState();
      this.loadOutputRows(buildCaseNoteHashRewriteAuditRows(reviewAnalysis, 'Review confirmed'), {
        title: 'Note hash-to-path-label rewrite audit',
        detail: 'Note backup/rewrite step was marked complete for the current scan.',
        sourceActionId: 'backup_notes_replace_hashes',
        target: 'audit-only'
      });
      this.noteHashRewriteStatus = 'Original notes backup and hash-to-path-label rewrite marked complete for the current scan.';
      this.showCaseToast(this.noteHashRewriteStatus);
      return true;
    },

    stageOriginalNotesBackupPreview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging original notes backup…',
          message: 'Building backup rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Original notes backup staged.'
        }, () => this.stageOriginalNotesBackupPreview({ ...options, __caseOperationWrapped: true }));
      }
      const review = this.caseSupportState.workflowState.noteHashPathLabelRewriteReview || {};
      let backup = review.backup || null;
      if (!backup?.rows?.length) {
        backup = buildCaseNoteHashRewriteBackupRecord(this.getMatrixForLogicalSheet('main'), {
          caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
          revision: Number(this.caseSupportState.caseDataRevision) || 0
        });
      }
      const rows = buildCaseNoteHashRewriteBackupOutputRows(backup);
      this.loadOutputRows(rows, {
        title: 'Original Notes Backup preview',
        detail: 'Original Column J notes backup staged in Output for review/download.',
        sourceDeliverableId: 'original_notes_backup',
        target: 'deliverable-preview'
      });
      const deliverable = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(item => item.id === 'original_notes_backup');
      if (deliverable) this.markDeliverableBuilt(deliverable);
      if (!options.silent) {
        this.noteHashRewriteStatus = `${Math.max(0, rows.length - 3)} original notes backup row${rows.length === 4 ? '' : 's'} staged in Output.`;
        this.showCaseToast(this.noteHashRewriteStatus);
      }
      return rows;
    },

    downloadOriginalNotesBackup() {
      const review = this.caseSupportState.workflowState.noteHashPathLabelRewriteReview || {};
      const backup = review.backup || buildCaseNoteHashRewriteBackupRecord(this.getMatrixForLogicalSheet('main'), {
        caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
        revision: Number(this.caseSupportState.caseDataRevision) || 0
      });
      downloadTextFile('original_notes_backup.tsv', buildTsv(buildCaseNoteHashRewriteBackupOutputRows(backup)), 'text/tab-separated-values;charset=utf-8;');
      this.noteHashRewriteStatus = 'Original Notes Backup downloaded.';
      this.showCaseToast(this.noteHashRewriteStatus);
    },

    restoreOriginalNotesBackupToMain() {
      const backup = this.caseSupportState.workflowState.noteHashPathLabelRewriteReview?.backup || null;
      if (!backup?.rows?.length) {
        this.noteHashRewriteStatus = 'No Original Notes Backup exists to restore.';
        return null;
      }
      const confirmed = window.confirm('Restore Column J notes from the Original Notes Backup where row number, hash, and path label still appear to match? Rows whose identity changed will be skipped.');
      if (!confirmed) return null;
      const mainSheet = this.findLogicalSheetMeta('main');
      if (!mainSheet) {
        this.noteHashRewriteStatus = 'Main Data sheet was not found.';
        return null;
      }
      const state = this.getSheetStateById(mainSheet.id);
      const result = restoreCaseNoteHashRewriteBackupToMainMatrix(state.data, backup);
      if (result.changed) {
        this.writeSheetStateById(mainSheet.id, {
          ...state,
          data: result.data,
          frozenTop: 1
        });
        this.markCaseDataMutated('original_notes_backup_restored', {
          rowsRestored: result.rowsRestored,
          skipped: result.skippedCount
        });
      }
      this.noteHashRewriteLastRunSummary = null;
      this.refreshNoteHashRewrite({ silent: true });
      this.noteHashRewriteStatus = result.changed
        ? `Restored ${result.rowsRestored} Column J note${result.rowsRestored === 1 ? '' : 's'} from backup. ${result.skippedCount} backup row${result.skippedCount === 1 ? '' : 's'} skipped because their row identity changed.`
        : `No notes restored. ${result.skippedCount} backup row${result.skippedCount === 1 ? '' : 's'} skipped or already matched.`;
      this.showCaseToast(this.noteHashRewriteStatus);
      return result;
    },

    selectNoteHashRewriteRow(item) {
      if (!item || !Number.isInteger(item.rowIndex)) return false;
      this.activateLogicalSheet('main');
      this.$nextTick(() => {
        const sheet = this.getInputSheet?.();
        sheet?.selectCell?.(item.rowIndex, 9);
        sheet?.hotInstance?.scrollViewportTo?.(item.rowIndex, 9, true, true);
      });
      this.noteHashRewriteStatus = `Selected Main Data row ${item.rowNumber}, Column J.`;
      return true;
    },

    async copyNoteHashRewriteDetails(item) {
      const text = [
        `Row: ${item?.rowNumber || ''}`,
        `Path: ${item?.pathLabel || ''}`,
        `Hash: ${item?.transactionHash || ''}`,
        `Column Q: ${item?.continuesAs || ''}`,
        `Q labels: ${(item?.qLabels || []).join(', ')}`,
        `Unresolved: ${(item?.unresolvedHashes || []).join(', ')}`,
        '',
        'Current notes:',
        item?.currentNotes || '',
        '',
        'Proposed notes:',
        item?.proposedNotes || ''
      ].join('\n');
      const copied = await this.copyTextToClipboard(text, 'Note rewrite details copied.');
      this.noteHashRewriteStatus = copied ? 'Note rewrite details copied.' : 'Could not copy note rewrite details in this browser.';
      return copied;
    },

    async openAddressLegendModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening address legend…',
        message: 'Building an address legend preview from current Main Data.',
        target: 'address-legend-modal',
        finishMessage: 'Address legend preview ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.addressLegendSearch = '';
        this.addressLegendViewFilter = options.view || 'changed';
        this.addressLegendStatus = 'Building an address legend preview from current Main Data From/To addresses.';
        const existingReview = this.caseSupportState.workflowState.addressLegendReview || {};
        this.addressLegendLabelDormantSeparately = !!existingReview.labelDormantAddressesSeparately;
        this.addressLegendUseDepositLabels = !!existingReview.useDepositAddressLabels;
        if (options.forceRemote || this.entityCuratorRemote.status === 'idle') {
          await this.loadEntityCuratorRemoteKeywords({ force: !!options.forceRemote, silent: true });
        }
        this.showAddressLegendModal = true;
        await this.yieldToCaseUi();
        await this.refreshAddressLegendReview({ silent: true });
        return true;
      });
    },

    cancelAddressLegendModal() {
      if (this.addressLegendRunning) return;
      this.showAddressLegendModal = false;
    },

    async refreshAddressLegendReview(options = {}) {
      if (options.forceRemote) {
        await this.loadEntityCuratorRemoteKeywords({ force: true, silent: !!options.silent });
      }
      this.entityCuratorLocalOverlay = loadEntityCuratorOverlayFromStorage();
      this.clearCasePerformanceCache?.();
      const analysis = buildCaseAddressLegendAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        {
          labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately,
          useDepositAddressLabels: this.addressLegendUseDepositLabels
        }
      );
      this.addressLegendCandidates = analysis.candidates || [];
      if (!this.addressLegendCandidates.some(candidate => candidate.id === this.addressLegendSelectedCandidateId)) {
        this.addressLegendSelectedCandidateId = this.addressLegendCandidates[0]?.id || '';
      }
      this.addressLegendLastRunSummary = analysis;
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        address_legend: this.buildAddressLegendReviewCheckResult(analysis)
      };
      this.persistCaseSupportState();
      if (!options.silent) this.addressLegendStatus = analysis.summary;
      else if (!this.addressLegendStatus) this.addressLegendStatus = analysis.summary;
      return analysis;
    },

    selectAddressLegendCandidate(candidate = {}) {
      this.addressLegendSelectedCandidateId = candidate?.id || candidate?.key || '';
    },

    buildTemplateDeliverableCheckResult() {
      const state = this.getCaseTemplateState();
      const recordStatus = this.getTemplateDeliverableRecordStatus(state);
      const hasBuildRecord = !!recordStatus.builtAtUtc || recordStatus.builtRevision !== null;

      if (!state.hasAnyMainRows) {
        return this.buildCheckResult('template_deliverable', 'warn', 'Template needs Main Data rows before it can be built.', state);
      }

      if (!state.legendBuildIsCurrent) {
        return this.buildCheckResult('template_deliverable', 'fail', 'Template needs the address legend to be built from current Main Data first.', state);
      }

      if (!state.legendReviewIsCurrent) {
        return this.buildCheckResult('template_deliverable', 'fail', 'Template needs the address legend to be reviewed for the current case data first.', state);
      }

      if (!state.buildIsCurrent) {
        return this.buildCheckResult('template_deliverable', 'warn', state.summary || 'Template prerequisites are not current.', state);
      }

      if (recordStatus.builtIsCurrent) {
        if (!recordStatus.reviewIsCurrent) {
          return this.buildCheckResult('template_deliverable', 'warn', `Template deliverable is built current (${state.rowCount} row${state.rowCount === 1 ? '' : 's'}), but it has not been marked reviewed yet.`, state);
        }
        return this.buildCheckResult('template_deliverable', 'pass', `Template deliverable is current and reviewed (${state.rowCount} row${state.rowCount === 1 ? '' : 's'}).`, state);
      }

      if (hasBuildRecord) {
        return this.buildCheckResult('template_deliverable', 'fail', 'Template deliverable is stale because Main Data, the reviewed address legend, or the effective curator changed. Rebuild it.', state);
      }

      return this.buildCheckResult('template_deliverable', 'warn', state.summary || 'Template is ready to build.', state);
    },

    buildAddressLegendReviewCheckResult(analysis = null) {
      const reviewAnalysis = analysis || buildCaseAddressLegendAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        {
          labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately,
          useDepositAddressLabels: this.addressLegendUseDepositLabels
        }
      );

      if ((reviewAnalysis.candidateCount || 0) === 0) {
        return this.buildCheckResult('address_legend', 'fail', 'No From/To addresses were found in Main Data for the address legend.', reviewAnalysis);
      }

      if (!reviewAnalysis.buildIsCurrent) {
        return this.buildCheckResult(
          'address_legend',
          'fail',
          `${reviewAnalysis.candidateCount} address legend entr${reviewAnalysis.candidateCount === 1 ? 'y is' : 'ies are'} proposed, but the stored legend is not current. Build the legend, review it, then mark it reviewed.`,
          reviewAnalysis
        );
      }

      if (!reviewAnalysis.reviewIsCurrent) {
        return this.buildCheckResult(
          'address_legend',
          'warn',
          `Address legend is built for the current case data but has not been marked reviewed yet. Review ${reviewAnalysis.candidateCount} entr${reviewAnalysis.candidateCount === 1 ? 'y' : 'ies'} and confirm when it looks right.`,
          reviewAnalysis
        );
      }

      return this.buildCheckResult(
        'address_legend',
        'pass',
        `Address legend is built and reviewed for the current case data (${reviewAnalysis.candidateCount} entr${reviewAnalysis.candidateCount === 1 ? 'y' : 'ies'}).`,
        reviewAnalysis
      );
    },

    async buildCurrentAddressLegend(options = {}) {
      if (this.addressLegendRunning) return null;
      this.addressLegendRunning = true;
      try {
        const analysis = await this.refreshAddressLegendReview({ silent: true });
        if ((analysis.candidateCount || 0) === 0) {
          this.addressLegendStatus = 'No From/To addresses were found in Main Data to build into a legend.';
          return null;
        }

        const entries = buildCaseAddressLegendEntriesFromCandidates(analysis.candidates || []);
        this.caseSupportState.workflowState.addressLegendReview = {
          ...(this.caseSupportState.workflowState.addressLegendReview || {}),
          entries,
          builtBasisHash: analysis.currentBasisHash,
          builtAtUtc: new Date().toISOString(),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          candidateCount: analysis.candidateCount || 0,
          changedCount: analysis.changedCount || 0,
          staleLegendEntryCount: analysis.staleLegendEntryCount || 0,
          labelDormantAddressesSeparately: !!this.addressLegendLabelDormantSeparately,
          useDepositAddressLabels: !!this.addressLegendUseDepositLabels
        };
        this.addressLegendLastRunSummary = null;
        const refreshed = await this.refreshAddressLegendReview({ silent: true });
        this.caseSupportState.checkResultsById = {
          ...(this.caseSupportState.checkResultsById || {}),
          address_legend: this.buildAddressLegendReviewCheckResult(refreshed)
        };
        this.persistCaseSupportState();
        this.loadOutputRows(buildCaseAddressLegendAuditRows(refreshed, 'Legend built / refreshed'), {
          title: 'Address Legend preview',
          detail: 'Address legend built into separate per-case support state and staged in Output for review.',
          sourceActionId: 'build_address_legend',
          sourceDeliverableId: 'address_legend',
          target: 'deliverable-preview'
        });
        const deliverable = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(item => item.id === 'address_legend');
        if (deliverable) this.markDeliverableBuilt(deliverable);
        this.recordWorkflowToolRun('address_legend_build', {
          entries: entries.length,
          basisHash: refreshed.currentBasisHash,
          source: options.source || 'address-legend-modal'
        });
        this.addressLegendStatus = `Built address legend with ${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}. Review the preview, then mark the legend reviewed when it looks right.`;
        this.showCaseToast(this.addressLegendStatus);
        return refreshed;
      } finally {
        this.addressLegendRunning = false;
      }
    },

    rebuildAddressLegend(options = {}) {
      return this.buildCurrentAddressLegend(options);
    },

    downloadAddressLegendTsv() {
      return this.downloadAddressLegend();
    },

    markAddressLegendReviewComplete(options = {}) {
      return this.markAddressLegendReviewed(null, options);
    },

    async loadReviewedAddressLegendToOutput(options = {}) {
      void options;
      const review = this.caseSupportState.workflowState?.addressLegendReview || {};
      const storedEntries = Array.isArray(review.entries) ? review.entries : [];
      const currentRevision = Number(this.caseSupportState.caseDataRevision) || 0;
      const reviewedRevision = Number.isFinite(Number(review.reviewedRevision))
        ? Number(review.reviewedRevision)
        : Number.isFinite(Number(review.revision))
          ? Number(review.revision)
          : Number.isFinite(Number(review.builtRevision))
            ? Number(review.builtRevision)
            : null;
      const builtBasisHash = normalizeCell(review.builtBasisHash);
      const reviewedBasisHash = normalizeCell(review.reviewedBasisHash || review.confirmedReviewBasisHash);
      const reviewedCurrent = storedEntries.length > 0 && !!review.reviewedAtUtc && reviewedRevision === currentRevision && !!builtBasisHash && builtBasisHash === reviewedBasisHash;
      if (!reviewedCurrent) {
        this.addressLegendStatus = 'Address legend is not reviewed/current yet. Build and review the address legend before loading the quick reference.';
        this.showCaseToast(this.addressLegendStatus);
        return [];
      }

      const rows = buildCaseAddressLegendQuickAccessRows({}, { entries: storedEntries });
      this.loadOutputRows(rows, {
        title: 'Address Legend quick reference',
        detail: 'Reviewed address legend loaded as Address + Label rows for copying, export, and external tools.',
        sourceActionId: 'address_legend_quick_access',
        sourceDeliverableId: 'address_legend',
        target: 'reference-only'
      });
      this.scrollToSection('case-output');
      this.addressLegendStatus = `Loaded ${Math.max(0, rows.length - 1)} address legend entr${rows.length === 2 ? 'y' : 'ies'} into Output.`;
      this.showCaseToast(this.addressLegendStatus);
      return rows;
    },

    async stageAddressLegendPreview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging address legend…',
          message: 'Building address legend rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Address legend staged.'
        }, () => this.stageAddressLegendPreview({ ...options, __caseOperationWrapped: true }));
      }
      const analysis = await this.refreshAddressLegendReview({ silent: true });
      const rows = buildCaseAddressLegendAuditRows(analysis, options.actionLabel || 'Legend preview staged');
      this.loadOutputRows(rows, {
        title: 'Address Legend preview',
        detail: analysis.buildIsCurrent
          ? 'Current built address legend staged in Output for review/download.'
          : 'Proposed address legend staged in Output. Build it before marking the workflow step reviewed.',
        sourceActionId: 'stage_address_legend',
        sourceDeliverableId: 'address_legend',
        target: 'deliverable-preview'
      });
      const deliverable = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(item => item.id === 'address_legend');
      if (deliverable && analysis.buildIsCurrent) this.markDeliverableBuilt(deliverable);
      if (!options.silent) {
        this.addressLegendStatus = `${analysis.candidateCount || 0} address legend entr${(analysis.candidateCount || 0) === 1 ? 'y' : 'ies'} staged in Output.`;
        this.showCaseToast(this.addressLegendStatus);
      }
      return rows;
    },

    async downloadAddressLegend() {
      const analysis = await this.refreshAddressLegendReview({ silent: true });
      const rows = buildCaseAddressLegendDeliverableRows(analysis);
      downloadTextFile('address_legend.tsv', buildTsv(rows), 'text/tab-separated-values;charset=utf-8;');
      const deliverable = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(item => item.id === 'address_legend');
      if (deliverable && analysis.buildIsCurrent) this.markDeliverableBuilt(deliverable);
      this.addressLegendStatus = 'Address legend TSV downloaded.';
      this.showCaseToast(this.addressLegendStatus);
    },

    async markAddressLegendReviewed(analysis = null, options = {}) {
      const reviewAnalysis = analysis || await this.refreshAddressLegendReview({ silent: true });
      if (!reviewAnalysis.buildIsCurrent) {
        this.addressLegendStatus = 'Build or refresh the address legend first. Manual workflow completion is still available if you intentionally need to override.';
        return false;
      }

      this.caseSupportState.workflowState.addressLegendReview = {
        ...(this.caseSupportState.workflowState.addressLegendReview || {}),
        reviewedBasisHash: reviewAnalysis.currentBasisHash,
        reviewedAtUtc: new Date().toISOString(),
        reviewedRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        candidateCount: reviewAnalysis.candidateCount || 0,
        labelDormantAddressesSeparately: !!this.addressLegendLabelDormantSeparately,
        useDepositAddressLabels: !!this.addressLegendUseDepositLabels
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_ADDRESS_LEGEND_REVIEWED, options.source || 'build_address_legend', { silent: true });
      this.addressLegendLastRunSummary = null;
      const refreshed = await this.refreshAddressLegendReview({ silent: true });
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        address_legend: this.buildAddressLegendReviewCheckResult(refreshed)
      };
      const deliverable = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(item => item.id === 'address_legend');
      if (deliverable) {
        this.markDeliverableBuilt(deliverable);
        this.caseSupportState.deliverableStateById = {
          ...(this.caseSupportState.deliverableStateById || {}),
          address_legend: {
            ...(this.caseSupportState.deliverableStateById?.address_legend || {}),
            builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
            builtAtUtc: this.caseSupportState.deliverableStateById?.address_legend?.builtAtUtc || new Date().toISOString(),
            reviewedAtUtc: new Date().toISOString()
          }
        };
      }
      this.persistCaseSupportState();
      this.loadOutputRows(buildCaseAddressLegendAuditRows(refreshed, 'Review confirmed'), {
        title: 'Address Legend review audit',
        detail: 'Address legend review was confirmed for the current case data.',
        sourceActionId: 'build_address_legend',
        sourceDeliverableId: 'address_legend',
        target: 'audit-only'
      });
      this.addressLegendStatus = 'Address legend marked reviewed for the current case data.';
      this.showCaseToast(this.addressLegendStatus);
      return true;
    },

    selectAddressLegendCandidateRow(candidate) {
      if (!candidate || !Number.isInteger(candidate.firstSeenRowIndex)) return false;
      this.activateLogicalSheet('main');
      this.$nextTick(() => {
        const sheet = this.getInputSheet?.();
        const col = candidate.sourceSide === 'from' ? 2 : 3;
        sheet?.selectCell?.(candidate.firstSeenRowIndex, col);
        sheet?.hotInstance?.scrollViewportTo?.(candidate.firstSeenRowIndex, col, true, true);
      });
      this.addressLegendStatus = `Selected Main Data row ${candidate.firstSeenRowNumber}, ${candidate.sourceSide === 'from' ? 'Column C' : 'Column D'}.`;
      return true;
    },

    async copyAddressLegendCandidateDetails(candidate) {
      const text = [
        `Label: ${candidate?.proposedLegendLabel || ''}`,
        `Address: ${candidate?.preferredDisplayAddress || ''}`,
        `Existing: ${candidate?.existingLegendLabel || ''}`,
        `Family: ${candidate?.labelFamilyLabel || candidate?.labelFamily || ''}`,
        `Source: ${candidate?.sourceSideLabel || ''} / ${candidate?.sourceReasonLabel || ''}`,
        `First row: ${candidate?.firstSeenRowNumber || ''}`,
        `Chain: ${candidate?.chainId || ''} ${candidate?.chainName || ''}`,
        '',
        'Evidence:',
        ...(candidate?.evidence || [])
      ].join('\n');
      const copied = await this.copyTextToClipboard(text, 'Address legend candidate copied.');
      this.addressLegendStatus = copied ? 'Address legend candidate copied.' : 'Could not copy address legend candidate in this browser.';
      return copied;
    },

    selectDepositPatternCandidateRow(candidate) {
      if (!candidate) return false;
      const rowIndex = Number.isInteger(candidate.childOutputRowIndex) ? candidate.childOutputRowIndex : candidate.childRowNumber - 1;
      if (!Number.isInteger(rowIndex) || rowIndex < 0) return false;
      this.activateLogicalSheet('main');
      this.$nextTick(() => {
        const sheet = this.getInputSheet?.();
        sheet?.selectCell?.(rowIndex, 0);
        sheet?.hotInstance?.scrollViewportTo?.(rowIndex, 0, true, true);
      });
      this.depositPatternStatus = `Selected Main Data row ${rowIndex + 1}.`;
      return true;
    },

    openDepositPatternCandidatePathOperation(candidate, action = 'prune') {
      if (!candidate) return false;
      const rowIndex = Number.isInteger(candidate.childOutputRowIndex) ? candidate.childOutputRowIndex : candidate.childRowNumber - 1;
      if (!Number.isInteger(rowIndex) || rowIndex < 0) return false;
      this.showDepositPatternModal = false;
      this.openPathOperationModal({ rowIndex, action, source: 'deposit-pattern-review' });
      return true;
    },

    async copyDepositPatternCandidateSearch(candidate) {
      const text = [
        `${candidate.parentHash || ''} -> ${candidate.childHash || ''}`,
        `Rows ${candidate.parentRowNumber || ''} -> ${candidate.childOutputRowNumber || candidate.childRowNumber || ''}`,
        `${candidate.grade || ''} ${candidate.token || ''} ${candidate.amountAFormatted || ''} -> ${candidate.amountBFormatted || ''}`,
        candidate.childNotes || '',
        candidate.childAttribution || ''
      ].filter(Boolean).join('\n');
      try {
        await navigator.clipboard.writeText(text);
        this.depositPatternStatus = 'Deposit-pattern search text copied.';
      } catch {
        this.depositPatternStatus = 'Could not copy search text in this browser.';
      }
    },

    async openEntityCuratorReviewModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening entity curator review…',
        message: 'Reviewing Main Data for known and newly observed entities.',
        target: 'entity-curator-modal',
        finishMessage: 'Entity curator review ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.entityCuratorLocalOverlay = loadEntityCuratorOverlayFromStorage();
        this.clearCasePerformanceCache?.();
        this.entityCuratorStatus = 'Reviewing current Main Data for known/new entity candidates.';
        this.entityCuratorActiveTab = options?.tab || 'review';
        if (options?.forceRemote || this.entityCuratorRemote.status === 'idle') {
          await this.loadEntityCuratorRemoteKeywords({ force: !!options?.forceRemote });
        }
        this.showEntityCuratorModal = true;
        await this.yieldToCaseUi();
        this.refreshEntityCuratorReview({ silent: true });
        return true;
      });
    },

    cancelEntityCuratorReviewModal() {
      if (this.entityCuratorLoading) return;
      this.showEntityCuratorModal = false;
    },

    async copyTextToClipboard(text, successMessage = 'Copied to clipboard.') {
      const payload = String(text || '');
      if (!payload) return false;
      if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(payload);
          if (successMessage) this.showCaseToast(successMessage);
          return true;
        } catch {
          // Fall through to textarea fallback.
        }
      }

      if (typeof document !== 'undefined') {
        const textarea = document.createElement('textarea');
        textarea.value = payload;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (copied && successMessage) this.showCaseToast(successMessage);
        return copied;
      }

      return false;
    },

    async fetchEntityCuratorKeywordEndpoint(url, label) {
      const response = await authenticatedFetch(url, { method: 'GET' });
      if (!response.ok) {
        throw new Error(`${label} curator endpoint returned HTTP ${response.status}.`);
      }
      const data = await response.json();
      if (data?.status !== 'success') {
        throw new Error(data?.message || `${label} curator endpoint returned an error.`);
      }
      return normalizeEntityCuratorList(data.keywords || []);
    },

    async loadEntityCuratorRemoteKeywords(options = {}) {
      const force = !!options.force;
      const silent = !!options.silent;
      if (this.entityCuratorLoading) return this.entityCuratorRemote;
      if (!force && this.entityCuratorRemote.status === 'success' && this.entityCuratorRemote.exchange.length && this.entityCuratorRemote.nonExchange.length) {
        return this.entityCuratorRemote;
      }

      this.entityCuratorLoading = true;
      if (!silent) this.entityCuratorStatus = 'Loading server curator lists…';

      try {
        const [exchange, nonExchange] = await Promise.all([
          this.fetchEntityCuratorKeywordEndpoint(CASE_ENTITY_CURATOR_REMOTE_EXCHANGE_ENDPOINT, 'Exchange'),
          this.fetchEntityCuratorKeywordEndpoint(CASE_ENTITY_CURATOR_REMOTE_NON_EXCHANGE_ENDPOINT, 'Non-exchange')
        ]);
        this.entityCuratorRemote = {
          exchange,
          nonExchange,
          lastLoadedUtc: new Date().toISOString(),
          status: 'success',
          message: `Loaded ${exchange.length} exchange and ${nonExchange.length} non-exchange server curator term${exchange.length + nonExchange.length === 1 ? '' : 's'}.`
        };
        this.clearCasePerformanceCache?.();
        if (!silent) this.entityCuratorStatus = this.entityCuratorRemote.message;
      } catch (error) {
        this.entityCuratorRemote = {
          ...this.entityCuratorRemote,
          status: this.entityCuratorRemote.exchange.length || this.entityCuratorRemote.nonExchange.length ? 'stale' : 'error',
          message: error?.message || 'Failed to load server curator lists.'
        };
        if (!silent) this.entityCuratorStatus = this.entityCuratorRemote.message;
      } finally {
        this.entityCuratorLoading = false;
      }

      return this.entityCuratorRemote;
    },

    async refreshEntityCuratorReview(options = {}) {
      if (options.forceRemote) {
        await this.loadEntityCuratorRemoteKeywords({ force: true, silent: !!options.silent });
      }
      this.entityCuratorLocalOverlay = loadEntityCuratorOverlayFromStorage();
      this.clearCasePerformanceCache?.();
      const analysis = this.entityCuratorAnalysis;
      if (!options.silent) this.entityCuratorStatus = analysis.summary;
      return analysis;
    },

    setEntityCuratorOverlay(overlay = {}, status = '') {
      this.entityCuratorLocalOverlay = saveEntityCuratorOverlayToStorage(overlay);
      this.clearCasePerformanceCache?.();
      const analysis = this.entityCuratorAnalysis;
      if (status) this.entityCuratorStatus = status;
      this.entityCuratorLastReviewSummary = {
        reviewable: analysis.reviewableGroupCount,
        known: analysis.knownGroupCount,
        suppressed: analysis.suppressedGroupCount,
        basisHash: analysis.currentBasisHash
      };
      return analysis;
    },

    addLocalCuratorTerm(kind, name, options = {}) {
      const term = normalizeCell(name);
      if (!term) return null;
      const overlay = normalizeEntityCuratorOverlay(this.entityCuratorLocalOverlay);
      if (kind === CASE_ENTITY_KIND_EXCHANGE) {
        overlay.exchangeAdditions = addEntityCuratorTermToList(overlay.exchangeAdditions, term);
        overlay.exchangeRemovals = removeEntityCuratorTermFromList(overlay.exchangeRemovals, term);
      } else if (kind === CASE_ENTITY_KIND_NON_EXCHANGE) {
        overlay.nonExchangeAdditions = addEntityCuratorTermToList(overlay.nonExchangeAdditions, term);
        overlay.nonExchangeRemovals = removeEntityCuratorTermFromList(overlay.nonExchangeRemovals, term);
      } else {
        return null;
      }
      overlay.suppressedTerms = removeEntityCuratorTermFromList(overlay.suppressedTerms, term);
      const kindLabel = formatCaseEntityKindLabel(kind);
      const analysis = this.setEntityCuratorOverlay(overlay, options.status || `Added “${term}” to the local ${kindLabel} curator list.`);
      this.recordWorkflowToolRun('local_known_entity_curator_update', { action: 'add', term, kind, source: options.source || 'manual' });
      return analysis;
    },

    addManualExchangeCuratorTerm() {
      const term = normalizeCell(this.entityCuratorNewExchangeName);
      if (!term) return;
      this.addLocalCuratorTerm(CASE_ENTITY_KIND_EXCHANGE, term);
      this.entityCuratorNewExchangeName = '';
    },

    addManualNonExchangeCuratorTerm() {
      const term = normalizeCell(this.entityCuratorNewNonExchangeName);
      if (!term) return;
      this.addLocalCuratorTerm(CASE_ENTITY_KIND_NON_EXCHANGE, term);
      this.entityCuratorNewNonExchangeName = '';
    },

    addManualSuppressedEntityTerm() {
      const term = normalizeCell(this.entityCuratorNewSuppression);
      if (!term) return;
      this.suppressEntityCuratorTerm(term, { source: 'manual' });
      this.entityCuratorNewSuppression = '';
    },

    addEntityCandidateToCurator(group, kind) {
      if (!group || !kind) return;
      this.addLocalCuratorTerm(kind, group.displayName, { source: 'candidate', status: `Added candidate “${group.displayName}” as ${formatCaseEntityKindLabel(kind)} in your local curator overlay.` });
    },

    getEntityCuratorCaseMismatchGroups() {
      return (this.entityCuratorAnalysis.groups || []).filter(group => group?.isCaseMismatch && group?.canApplyCaseMismatchCasingFix);
    },

    getEntityCuratorCaseMismatchKnownCasingCandidates(group = null) {
      const groups = group ? [group] : this.getEntityCuratorCaseMismatchGroups();
      const rowsByCanonical = new Map();

      groups.forEach((item) => {
        const canonicalKey = normalizeCaseEntityTerm(item?.caseMismatchName || item?.matchedCanonicalName || '');
        if (!canonicalKey) return;
        const rows = rowsByCanonical.get(canonicalKey) || new Set();
        (item.hits || []).forEach((hit) => {
          if (normalizeCell(hit?.sourceField).toLowerCase() !== 'system notes') return;
          const rowNumber = Number(hit?.sheetRowNumber) || 0;
          if (rowNumber > 0) rows.add(rowNumber);
        });
        if (rows.size > 0) rowsByCanonical.set(canonicalKey, rows);
      });

      if (rowsByCanonical.size === 0) return [];

      const casingAnalysis = buildCaseKnownEntityCasingReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
      );

      return (casingAnalysis.candidates || []).filter((candidate) => {
        if (candidate?.hasCurrentDecision) return false;
        const canonicalKey = normalizeCaseEntityTerm(candidate?.canonicalName || candidate?.presentedName || '');
        const rows = rowsByCanonical.get(canonicalKey);
        return !!rows && rows.has(Number(candidate?.rowNumber) || 0);
      });
    },

    async applyEntityCuratorCaseMismatchCasingFix(group, options = {}) {
      if (!group?.isCaseMismatch) return false;
      const candidates = this.getEntityCuratorCaseMismatchKnownCasingCandidates(group);
      if (candidates.length === 0) {
        this.entityCuratorStatus = group?.caseMismatchName
          ? `No editable Column J casing fix was found for “${group.displayName}”. If this came from Column K attribution, update the cell manually or leave this card reviewed.`
          : 'No editable Column J casing fix was found for this card.';
        return false;
      }

      const result = this.applyKnownEntityCasingFixes(candidates, {
        skipConfirm: options.skipConfirm !== false,
        source: options.source || 'entity-curator-case-match'
      });
      if (result && typeof result.then === 'function') await result;
      const analysis = this.refreshEntityCuratorReview({ silent: true });
      this.refreshEntityReviewCheckResult(analysis);
      this.entityCuratorStatus = `Matched ${candidates.length} Column J casing fix${candidates.length === 1 ? '' : 'es'} to curator term “${group.caseMismatchName}”. ${analysis.reviewableGroupCount || 0} reviewable candidate${(analysis.reviewableGroupCount || 0) === 1 ? '' : 's'} remain.`;
      return true;
    },

    async applyAllEntityCuratorCaseMismatchCasingFixes(options = {}) {
      const candidates = this.getEntityCuratorCaseMismatchKnownCasingCandidates(null);
      if (candidates.length === 0) {
        this.entityCuratorStatus = 'No exact known-entity casing fixes are available in Column J notes.';
        return false;
      }

      const result = this.applyKnownEntityCasingFixes(candidates, {
        skipConfirm: options.skipConfirm !== false,
        source: options.source || 'entity-curator-case-match-all'
      });
      if (result && typeof result.then === 'function') await result;
      const analysis = this.refreshEntityCuratorReview({ silent: true });
      this.refreshEntityReviewCheckResult(analysis);
      this.entityCuratorStatus = `Matched ${candidates.length} Column J casing fix${candidates.length === 1 ? '' : 'es'} to the effective curator. ${analysis.reviewableGroupCount || 0} reviewable candidate${(analysis.reviewableGroupCount || 0) === 1 ? '' : 's'} remain.`;
      this.showCaseToast(this.entityCuratorStatus);
      return true;
    },

    suppressEntityCuratorTerm(term, options = {}) {
      const cleanTerm = normalizeCell(term);
      if (!cleanTerm) return null;
      const overlay = normalizeEntityCuratorOverlay(this.entityCuratorLocalOverlay);
      overlay.suppressedTerms = addEntityCuratorTermToList(overlay.suppressedTerms, cleanTerm);
      overlay.exchangeAdditions = removeEntityCuratorTermFromList(overlay.exchangeAdditions, cleanTerm);
      overlay.nonExchangeAdditions = removeEntityCuratorTermFromList(overlay.nonExchangeAdditions, cleanTerm);
      const analysis = this.setEntityCuratorOverlay(overlay, options.status || `Suppressed “${cleanTerm}” locally as non-entity noise.`);
      this.recordWorkflowToolRun('local_known_entity_curator_update', { action: 'suppress', term: cleanTerm, source: options.source || 'manual' });
      return analysis;
    },

    suppressEntityCuratorCandidate(group) {
      if (!group) return;
      this.suppressEntityCuratorTerm(group.displayName, { source: 'candidate', status: `Suppressed candidate “${group.displayName}” locally as non-entity noise.` });
    },

    removeEntityCuratorItem(item) {
      if (!item?.name || !item?.kind) return;
      const overlay = normalizeEntityCuratorOverlay(this.entityCuratorLocalOverlay);
      if (item.kind === CASE_ENTITY_KIND_EXCHANGE) {
        overlay.exchangeAdditions = removeEntityCuratorTermFromList(overlay.exchangeAdditions, item.name);
        if (item.source !== 'local') overlay.exchangeRemovals = addEntityCuratorTermToList(overlay.exchangeRemovals, item.name);
      } else if (item.kind === CASE_ENTITY_KIND_NON_EXCHANGE) {
        overlay.nonExchangeAdditions = removeEntityCuratorTermFromList(overlay.nonExchangeAdditions, item.name);
        if (item.source !== 'local') overlay.nonExchangeRemovals = addEntityCuratorTermToList(overlay.nonExchangeRemovals, item.name);
      }
      const analysis = this.setEntityCuratorOverlay(overlay, `${item.source === 'local' ? 'Removed' : 'Hid'} “${item.name}” from the local effective ${formatCaseEntityKindLabel(item.kind)} curator list.`);
      this.recordWorkflowToolRun('local_known_entity_curator_update', { action: item.source === 'local' ? 'remove-local' : 'hide-remote', term: item.name, kind: item.kind });
      return analysis;
    },

    restoreRemoteCuratorTerm(kind, term) {
      const cleanTerm = normalizeCell(term);
      if (!cleanTerm) return;
      const overlay = normalizeEntityCuratorOverlay(this.entityCuratorLocalOverlay);
      if (kind === CASE_ENTITY_KIND_EXCHANGE) {
        overlay.exchangeRemovals = removeEntityCuratorTermFromList(overlay.exchangeRemovals, cleanTerm);
      } else if (kind === CASE_ENTITY_KIND_NON_EXCHANGE) {
        overlay.nonExchangeRemovals = removeEntityCuratorTermFromList(overlay.nonExchangeRemovals, cleanTerm);
      }
      this.setEntityCuratorOverlay(overlay, `Restored “${cleanTerm}” to the effective curator list.`);
    },

    unsuppressEntityCuratorTerm(term) {
      const cleanTerm = normalizeCell(term);
      if (!cleanTerm) return;
      const overlay = normalizeEntityCuratorOverlay(this.entityCuratorLocalOverlay);
      overlay.suppressedTerms = removeEntityCuratorTermFromList(overlay.suppressedTerms, cleanTerm);
      this.setEntityCuratorOverlay(overlay, `Removed local suppression for “${cleanTerm}”.`);
    },

    async copyEntityCuratorCandidateSearch(group) {
      const text = [group?.displayName, group?.suggestedKindLabel, ...(group?.hits || []).slice(0, 8).map(hit => `Row ${hit.sheetRowNumber}: ${hit.excerpt}`)]
        .map(item => normalizeCell(item))
        .filter(Boolean)
        .join('\n');
      await this.copyTextToClipboard(text, 'Candidate search text copied.');
    },

    async copyEntityCuratorReviewTsv() {
      const analysis = this.entityCuratorAnalysis;
      const rows = [
        ['Status', 'Display Name', 'Suggested Kind', 'Hits', 'First Row', 'First Path Label', 'First Hash', 'Summary'],
        ...(analysis.groups || []).map((group) => {
          const firstHit = group.hits?.[0] || {};
          return [group.status, group.displayName, group.suggestedKindLabel, group.hitCount, firstHit.sheetRowNumber || '', firstHit.pathLabel || '', firstHit.transactionHash || '', group.suggestedSummary || ''];
        })
      ];
      await this.copyTextToClipboard(buildTsv(rows), 'Entity review TSV copied.');
    },

    markEntityCuratorReviewCompleteFromModal() {
      const analysis = this.entityCuratorAnalysis;
      this.caseSupportState.workflowState.entityReview = {
        basisHash: analysis.currentBasisHash,
        confirmedAtUtc: new Date().toISOString(),
        reviewableGroupCount: analysis.reviewableGroupCount,
        knownGroupCount: analysis.knownGroupCount,
        suppressedGroupCount: analysis.suppressedGroupCount,
        localExchangeAdditions: analysis.localExchangeAdditions,
        localNonExchangeAdditions: analysis.localNonExchangeAdditions,
        localExchangeRemovals: analysis.localExchangeRemovals,
        localNonExchangeRemovals: analysis.localNonExchangeRemovals,
        localSuppressedTerms: analysis.localSuppressedTerms
      };
      this.persistCaseSupportState();
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_NEW_ENTITIES_REVIEWED, 'known_entity_curator_review', { silent: true });
      this.loadOutputRows(buildEntityCuratorAuditRows(analysis, 'Review confirmed'), {
        title: 'Known entity curator review audit',
        detail: 'Audit only. Server curator lists were read-only; local curator overlay decisions were stored in browser local storage.',
        sourceActionId: 'review_new_entities',
        target: 'audit-only'
      });
      this.refreshEntityReviewCheckResult(analysis);
      this.entityCuratorStatus = 'Entity curator review marked complete for the current visible candidate set.';
      this.showCaseToast('Entity curator review marked complete.');
    },

    buildEntityReviewCheckResult(analysis = null) {
      const reviewAnalysis = analysis || buildCaseEntityCuratorReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.entityCuratorRemote,
        this.entityCuratorLocalOverlay,
        this.caseSupportState.workflowState.entityReview
      );

      if (reviewAnalysis.totalGroupCount === 0) {
        return this.buildCheckResult('entity_review', 'warn', 'No attribution or system-note entity candidates were detected in Main Data yet.', reviewAnalysis);
      }

      if (reviewAnalysis.isCurrent) {
        return this.buildCheckResult(
          'entity_review',
          'pass',
          `Known entity curator review is current. ${reviewAnalysis.reviewableGroupCount} reviewable candidate${reviewAnalysis.reviewableGroupCount === 1 ? '' : 's'} remained visible when confirmed; ${reviewAnalysis.knownGroupCount} known match${reviewAnalysis.knownGroupCount === 1 ? '' : 'es'} and ${reviewAnalysis.suppressedGroupCount} suppressed/noise group${reviewAnalysis.suppressedGroupCount === 1 ? '' : 's'} were tracked.`,
          reviewAnalysis
        );
      }

      return this.buildCheckResult(
        'entity_review',
        reviewAnalysis.reviewableGroupCount > 0 ? 'fail' : 'warn',
        reviewAnalysis.reviewableGroupCount > 0
          ? `${reviewAnalysis.reviewableGroupCount} reviewable entity candidate${reviewAnalysis.reviewableGroupCount === 1 ? '' : 's'} need local curator review. The server curator is read-only here; add local exchange/non-exchange terms, suppress noise, or mark the review complete.`
          : 'No new entity candidates are visible, but this curator review has not been confirmed for the current candidate basis yet.',
        reviewAnalysis
      );
    },

    refreshEntityReviewCheckResult(analysis = null) {
      const result = this.buildEntityReviewCheckResult(analysis);
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        entity_review: result
      };
      this.persistCaseSupportState();
      return result;
    },

    async openPathOperationModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening path operation review…',
        message: 'Scanning output rows and path-ending candidates for prune, graft, and Bridge work.',
        target: 'path-operation-modal',
        finishMessage: 'Path operation review ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.pathOperationStatus = '';
        this.pathOperationLastRunSummary = null;
        this.pathOperationConfirmApply = false;

        const requestedAction = ['prune', 'graft', 'bridge'].includes(options?.action) ? options.action : '';
        if (requestedAction) {
          this.pathOperationAction = requestedAction;
        }

        this.showPathOperationModal = true;
        await this.yieldToCaseUi();
        const analysis = this.refreshPathOperationCandidates({ preserveSelection: !options?.candidateId && !Number.isInteger(options?.rowIndex), silent: true });
        let selectedCandidate = null;

        if (options?.candidateId) {
          selectedCandidate = this.pathOperationCandidates.find(candidate => candidate.id === options.candidateId) || null;
        }

        if (!selectedCandidate && Number.isInteger(options?.rowIndex)) {
          selectedCandidate = findExactPathOperationCandidateForRow(analysis, options.rowIndex);
        }

        if (selectedCandidate) {
          this.pathOperationShowPathEndingsOnly = selectedCandidate.isPathEnding;
          this.selectPathOperationCandidate(selectedCandidate.id);
          if (requestedAction && (requestedAction === 'prune' || selectedCandidate.isPathEnding)) {
            this.pathOperationAction = requestedAction;
          }
          this.pathOperationStatus = options?.source === 'context-menu'
            ? `Opened from Main Data row ${selectedCandidate.spreadsheetRowNumber}. ${this.pathOperationActionLabel} is selected.`
            : this.pathOperationStatus;
        } else if (!options?.candidateId && !Number.isInteger(options?.rowIndex)) {
          this.pathOperationShowPathEndingsOnly = true;
          this.pathOperationStatus = `${analysis.qualifiedCount} qualified output row${analysis.qualifiedCount === 1 ? '' : 's'} detected; ${analysis.pathEndingCount} path ending${analysis.pathEndingCount === 1 ? '' : 's'} can currently stage graft or Bridge graft work.`;
        }
        return true;
      });
    },

    markAttributionPathOperationsCompleteFromModal() {
      const manualState = this.caseSupportState.workflowState.manualCompleteByTag || {};
      manualState.ATTRIBUTION_PATH_OPERATIONS = {
        completedAtUtc: new Date().toISOString(),
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        source: 'path-operation-modal'
      };
      this.caseSupportState.workflowState.manualCompleteByTag = { ...manualState };
      this.persistCaseSupportState();
      this.pathOperationStatus = 'Path-operation review marked complete for the current case revision. You can return here later if new graft or Bridge work changes the case.';
      this.showCaseToast('Path-operation review marked complete.');
    },

    cancelPathOperationModal() {
      if (this.pathOperationRunning) return;
      this.showPathOperationModal = false;
    },

    refreshPathOperationCandidates(options = {}) {
      const preserveSelection = options.preserveSelection !== false;
      const previousSelectedId = preserveSelection ? this.pathOperationSelectedCandidateId : '';
      const analysis = buildPathOperationAnalysis(this.getMatrixForLogicalSheet('main'), this.getMatrixForLogicalSheet('raw'));
      this.pathOperationCandidates = analysis.candidates || [];
      const stillExists = previousSelectedId && this.pathOperationCandidates.some(candidate => candidate.id === previousSelectedId);
      this.pathOperationSelectedCandidateId = stillExists ? previousSelectedId : '';
      this.pathOperationSelectedRawSetId = '';
      this.pathOperationConfirmApply = false;
      if (!options.silent) {
        this.pathOperationStatus = `${analysis.qualifiedCount} qualified output row${analysis.qualifiedCount === 1 ? '' : 's'} detected; ${analysis.pathEndingCount} path ending${analysis.pathEndingCount === 1 ? '' : 's'} can currently stage graft or Bridge graft work.`;
      }
      return analysis;
    },

    selectPathOperationCandidate(candidateId) {
      const candidate = this.pathOperationCandidates.find(row => row.id === candidateId) || null;
      if (!candidate) {
        this.pathOperationSelectedCandidateId = '';
        this.pathOperationStatus = 'Selected path operation row could not be found. Refresh candidates and try again.';
        return null;
      }

      this.pathOperationSelectedCandidateId = candidate.id;
      this.pathOperationConfirmApply = false;
      this.pathOperationSelectedRawSetId = candidate.rawCandidates?.[0]?.id || '';
      this.pathOperationRawTsv = '';
      this.pathOperationBridgeReceivedAmount = '';
      if (this.pathOperationAction !== 'prune' && !candidate.isPathEnding) {
        this.pathOperationAction = 'prune';
      }
      if (!this.pathOperationBridgeService) {
        const bridgeMatch = findBestCaseBridgeServiceMatch(buildBridgeSearchTextFromPathOperationCandidate(candidate));
        if (bridgeMatch.isMatch) this.pathOperationBridgeService = bridgeMatch.canonicalName;
      }
      this.pathOperationStatus = `Selected Main Data row ${candidate.spreadsheetRowNumber}. ${candidate.isPathEnding ? 'It is a path ending and can stage graft/Bridge work.' : 'It is not a path ending, so graft/Bridge work is disabled until it is pruned or otherwise ended.'}`;
      return candidate;
    },

    selectPathOperationFromCurrentSelection() {
      const mainSheet = this.findLogicalSheetMeta('main');
      const activeSheetId = this.$refs.inputSpreadsheet?.activeSheetId || this.$refs.inputSpreadsheet?.activeSheet?.id || '';
      if (mainSheet?.id && activeSheetId && activeSheetId !== mainSheet.id) {
        this.pathOperationStatus = 'The active sheet is not Main Data. Open Main Data, select the intended output row, then try again.';
        return null;
      }

      const cell = this.getSelectedAnchorCell();
      if (!cell) {
        this.pathOperationStatus = 'No intentional spreadsheet cell is selected. Select a cell in the target output row first.';
        return null;
      }

      const analysis = this.refreshPathOperationCandidates({ silent: true, preserveSelection: true });
      const candidate = findPathOperationCandidateForRow(analysis, cell.row);
      if (!candidate) {
        this.pathOperationStatus = `Selected row ${cell.row + 1} is not a qualified output row. It needs Column M funds followed and Column R/S left-bounds data.`;
        return null;
      }

      this.selectPathOperationCandidate(candidate.id);
      this.pathOperationStatus = `Selected output row ${candidate.spreadsheetRowNumber} from the current spreadsheet selection.`;
      return candidate;
    },

    stageSelectedRawSetForPathOperation() {
      const candidate = this.pathOperationSelectedCandidate;
      if (!candidate || !this.pathOperationSelectedRawSetId) return;
      const rawSet = (candidate.rawCandidates || []).find(set => set.id === this.pathOperationSelectedRawSetId);
      if (!rawSet) return;
      this.pathOperationRawTsv = buildTsv(rawSet.rows || []);
      this.pathOperationStatus = `Loaded raw data set rooted at Raw Data row ${rawSet.displayRootRow} into the staging box.`;
    },

    copyPathOperationCandidatesTsv() {
      const tsv = buildPathOperationCandidatesTsv(this.pathOperationFilteredCandidates);
      if (!tsv.trim()) {
        this.pathOperationStatus = 'No path operation candidates are available to copy.';
        return;
      }

      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(tsv)
          .then(() => {
            this.pathOperationStatus = 'Path operation candidates TSV copied to clipboard.';
          })
          .catch(() => {
            this.loadOutputRows(tsv.split('\n').map(line => line.split('\t')), {
              title: 'Path operation candidates TSV',
              detail: 'Clipboard copy failed; candidates are staged here for copying.',
              sourceActionId: 'copy_path_operation_candidates',
              target: 'audit-only'
            });
            this.pathOperationStatus = 'Clipboard copy failed; candidates were staged in Output for copying.';
          });
        return;
      }

      this.loadOutputRows(tsv.split('\n').map(line => line.split('\t')), {
        title: 'Path operation candidates TSV',
        detail: 'Clipboard unavailable; candidates are staged here for copying.',
        sourceActionId: 'copy_path_operation_candidates',
        target: 'audit-only'
      });
      this.pathOperationStatus = 'Clipboard unavailable; candidates were staged in Output for copying.';
    },

    applySelectedPathPrune() {
      if (!this.pathOperationCanApplyPrune) {
        this.pathOperationStatus = 'Select a qualified output row and confirm the prune before applying.';
        return;
      }

      const candidate = this.pathOperationSelectedCandidate;
      const preview = this.pathOperationPrunePreview;
      const confirmed = window.confirm(`Apply prune to Main Data row ${candidate.spreadsheetRowNumber}? This will remove ${preview.rowRemovalCount} downstream row(s) in the same main transaction block and mark the selected output as Path ends.`);
      if (!confirmed) return;

      this.pathOperationRunning = true;
      try {
        const mainSheet = this.findLogicalSheetMeta('main');
        if (!mainSheet) throw new Error('Main Data sheet was not found.');

        const currentMainData = this.getMatrixForLogicalSheet('main');
        const result = buildPrunedMainDataForCandidate(currentMainData, candidate);
        const state = this.getSheetStateById(mainSheet.id);
        this.writeSheetStateById(mainSheet.id, {
          ...state,
          data: result.data,
          frozenTop: 1
        });

        this.markCaseDataMutated('path_output_pruned', {
          row: candidate.spreadsheetRowNumber,
          hash: candidate.hash,
          pathLabel: candidate.pathLabel,
          removedRows: result.preview.rowRemovalCount,
          removedEntries: result.preview.descendantEntryCount
        });
        this.recordWorkflowToolRun('path_operation_prune', {
          row: candidate.spreadsheetRowNumber,
          hash: candidate.hash,
          removedRows: result.preview.rowRemovalCount,
          removedEntries: result.preview.descendantEntryCount
        });
        this.recordWorkflowSignal(WORKFLOW_SIGNAL_PATH_OPERATION_RESOLVED, 'path_operation_prune', { silent: true });
        this.loadOutputRows(buildPathOperationAuditRows({ action: 'prune', candidate, preview: result.preview }), {
          title: 'Path prune audit',
          detail: 'Audit only. Main Data was mutated by pruning the selected qualified output row.',
          sourceActionId: 'path_operation_prune',
          target: 'audit-only'
        });

        this.pathOperationConfirmApply = false;
        this.pathOperationStatus = `Pruned row ${candidate.spreadsheetRowNumber}. Removed ${result.preview.rowRemovalCount} downstream row${result.preview.rowRemovalCount === 1 ? '' : 's'} and marked the selected output as Path ends.`;
        this.refreshPathOperationCandidates({ preserveSelection: false, silent: true });
        this.activateLogicalSheet('main');
        this.showCaseToast('Path prune applied to Main Data.');
      } catch (error) {
        this.pathOperationStatus = error?.message || 'Path prune failed.';
      } finally {
        this.pathOperationRunning = false;
      }
    },

    useDetectedBridgeService() {
      const match = this.pathOperationBridgeDetectedMatch;
      if (!match.isMatch) {
        this.pathOperationStatus = 'No known bridge service was detected from the selected row.';
        return;
      }

      this.pathOperationBridgeService = match.canonicalName;
      this.pathOperationStatus = `Bridge service set to ${match.canonicalName}. ${match.explorerUrl ? 'Explorer link is available below.' : 'Use the registry search hint below.'}`;
    },

    async copyPathOperationSelectedHash() {
      const hash = normalizeCell(this.pathOperationSelectedCandidate?.hash);
      if (!hash) {
        this.pathOperationStatus = 'No selected transaction hash is available to copy.';
        return;
      }

      const copied = await this.copyTextToClipboard(hash, 'Selected transaction hash copied.');
      this.pathOperationStatus = copied
        ? 'Selected transaction hash copied to clipboard.'
        : `Clipboard unavailable. Selected transaction hash: ${hash}`;
    },

    async copyPathOperationBridgeCalculatorValue(kind = 'followed') {
      const result = this.pathOperationBridgeCalculatorResult || {};
      if (!result.valid) {
        this.pathOperationStatus = result.message || 'Enter a valid post-bridge received amount before copying bridge calculator values.';
        return;
      }

      const isKit = kind === 'kit';
      const isLeftBounds = kind === 'leftBounds';
      const value = isKit
        ? [
          `Funds followed: ${result.followedDisplay}`,
          `Left bounds: ${result.leftBoundsNumericDisplay || result.leftBoundsDisplay}`
        ].join('\n')
        : isLeftBounds
          ? (result.leftBoundsNumericDisplay || result.leftBoundsDisplay)
          : result.followedDisplay;
      const label = isKit ? 'bridge calculation kit' : isLeftBounds ? 'left-bounds' : 'funds-followed';
      if (!value) {
        this.pathOperationStatus = `No calculated ${label} value is available to copy.`;
        return;
      }

      const copied = await this.copyTextToClipboard(value, `Calculated bridge ${label} copied.`);
      this.pathOperationStatus = copied
        ? `Calculated bridge ${label} copied${isKit ? '.' : `: ${value}`}`
        : `Clipboard unavailable. Calculated bridge ${label}: ${value}`;
    },

    copyPathOperationBridgeParentHash() {
      const hash = normalizeCell(this.pathOperationSelectedCandidate?.hash);
      if (!hash) {
        this.pathOperationStatus = 'No selected transaction hash is available to copy.';
        return;
      }

      if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(hash)
          .then(() => {
            this.pathOperationStatus = 'Selected parent transaction hash copied to clipboard.';
          })
          .catch(() => {
            this.pathOperationStatus = `Clipboard copy failed. Parent transaction hash: ${hash}`;
          });
        return;
      }

      this.pathOperationStatus = `Clipboard unavailable. Parent transaction hash: ${hash}`;
    },

    copyPathOperationBridgeSearchText() {
      const text = buildBridgeSearchClipboardText(
        this.pathOperationSelectedCandidate,
        this.pathOperationBridgeActiveMatch,
        this.pathOperationBridgeDestinationNetwork,
        this.pathOperationBridgeDestinationToken
      );

      if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => {
            this.pathOperationStatus = 'Bridge search kit copied to clipboard.';
          })
          .catch(() => {
            this.loadOutputRows(text.split('\n').map(line => line.split('\t')), {
              title: 'Bridge search kit',
              detail: 'Clipboard copy failed, so the bridge search kit was staged in Output for copying.',
              sourceActionId: 'copy_bridge_search_kit',
              target: 'audit-only'
            });
            this.pathOperationStatus = 'Clipboard copy failed; bridge search kit was staged in Output.';
          });
        return;
      }

      this.loadOutputRows(text.split('\n').map(line => line.split('\t')), {
        title: 'Bridge search kit',
        detail: 'Clipboard unavailable, so the bridge search kit was staged in Output for copying.',
        sourceActionId: 'copy_bridge_search_kit',
        target: 'audit-only'
      });
      this.pathOperationStatus = 'Clipboard unavailable; bridge search kit was staged in Output.';
    },

    stageSelectedPathExtension() {
      const candidate = this.pathOperationSelectedCandidate;
      if (!candidate) {
        this.pathOperationStatus = 'Select a path ending before applying graft or Bridge graft work.';
        return;
      }
      if (!candidate.isPathEnding) {
        this.pathOperationStatus = 'Graft and Bridge graft require a current Path ends row. Prune first if this output needs to become a path ending.';
        return;
      }
      if (!this.pathOperationConfirmApply) {
        this.pathOperationStatus = 'Confirm that this will mutate Main Data for the selected path only.';
        return;
      }

      if (!String(this.pathOperationRawTsv || '').trim() && this.pathOperationSelectedRawSetId) {
        this.stageSelectedRawSetForPathOperation();
      }

      const rawTsv = String(this.pathOperationRawTsv || '').trim();
      if (!rawTsv) {
        this.pathOperationStatus = 'Paste raw TSV or choose a matching raw data set before applying the graft.';
        return;
      }

      const action = this.pathOperationAction === 'bridge' ? 'bridge' : 'graft';
      const actionLabel = action === 'bridge' ? 'Bridge graft' : 'Graft';
      const confirmed = window.confirm(`Apply ${actionLabel} to Main Data row ${candidate.spreadsheetRowNumber}? This will process the selected raw data with Tool 68 and splice the resulting path rows into Main Data.`);
      if (!confirmed) return;

      const activeBridgeMatch = action === 'bridge' ? this.pathOperationBridgeActiveMatch : createCaseBridgeServiceMatch();
      const bridgeService = action === 'bridge'
        ? normalizeCaseBridgeServiceNameForStorage(this.pathOperationBridgeService || activeBridgeMatch.canonicalName || '')
        : this.pathOperationBridgeService;
      const bridge = {
        service: bridgeService,
        destinationNetwork: this.pathOperationBridgeDestinationNetwork,
        destinationToken: this.pathOperationBridgeDestinationToken,
        registryMatch: serializeCaseBridgeServiceMatch(activeBridgeMatch)
      };
      const actionId = action === 'bridge' ? 'path_operation_bridge_graft_applied' : 'path_operation_graft_applied';

      this.pathOperationRunning = true;
      try {
        const mainSheet = this.findLogicalSheetMeta('main');
        if (!mainSheet) throw new Error('Main Data sheet was not found.');

        const currentMainData = this.getMatrixForLogicalSheet('main');
        const result = buildPathOperationExtensionApplyResult(currentMainData, candidate, rawTsv, { action, bridge });
        const mainState = this.getSheetStateById(mainSheet.id);
        this.writeSheetStateById(mainSheet.id, { ...mainState, data: result.data, frozenTop: 1 });

        let rawAppendResult = null;
        const rawWasSelectedFromRawSheet = !!this.pathOperationSelectedRawSetId;
        if (!rawWasSelectedFromRawSheet && result.rawSelection?.rawRowsForRawSheet?.length > 0) {
          const rawSheet = this.findLogicalSheetMeta('raw');
          if (rawSheet) {
            const rawState = this.getSheetStateById(rawSheet.id);
            rawAppendResult = buildRawDataAppendMatrix(rawState.data, result.rawSelection.rawRowsForRawSheet);
            if (rawAppendResult.rowsAppended > 0) {
              this.writeSheetStateById(rawSheet.id, { ...rawState, data: rawAppendResult.data, frozenTop: 1 });
            }
          }
        }

        result.rawAppend = rawAppendResult;
        this.markCaseDataMutated(actionId, {
          row: candidate.spreadsheetRowNumber,
          hash: candidate.hash,
          pathLabel: candidate.pathLabel,
          insertedRows: result.splice?.insertedRows || 0,
          insertedSpreadsheetRows: result.splice?.insertedSpreadsheetRows || 0,
          rawRowsAppended: rawAppendResult?.rowsAppended || 0,
          bridge
        });
        this.recordWorkflowToolRun(actionId, {
          row: candidate.spreadsheetRowNumber,
          hash: candidate.hash,
          pathLabel: candidate.pathLabel,
          rawLineCount: rawTsv.split(/\r?\n/).filter(line => line.trim()).length,
          insertedRows: result.splice?.insertedRows || 0,
          insertedSpreadsheetRows: result.splice?.insertedSpreadsheetRows || 0,
          patchedContinuesAs: result.patchedContinuesAs,
          rawRowsAppended: rawAppendResult?.rowsAppended || 0,
          bridge
        });
        this.recordWorkflowSignal(WORKFLOW_SIGNAL_PATH_OPERATION_RESOLVED, actionId, { silent: true });
        this.loadOutputRows(buildPathOperationAuditRows({
          action,
          candidate,
          rawTsv,
          bridge,
          extensionResult: result,
          splice: result.splice,
          rawAppend: rawAppendResult
        }), {
          title: action === 'bridge' ? 'Bridge graft apply audit' : 'Graft apply audit',
          detail: 'Audit only. Main Data was mutated by applying the selected path extension.',
          sourceActionId: actionId,
          target: 'audit-only'
        });

        this.pathOperationConfirmApply = false;
        this.pathOperationRawTsv = '';
        this.pathOperationSelectedRawSetId = '';
        this.pathOperationStatus = result.message || `${actionLabel} applied to Main Data.`;
        this.refreshPathOperationCandidates({ preserveSelection: false, silent: true });
        this.activateLogicalSheet('main');
        this.showCaseToast(`${actionLabel} applied to Main Data.`);
      } catch (error) {
        this.pathOperationStatus = error?.message || `${actionLabel} failed.`;
      } finally {
        this.pathOperationRunning = false;
      }
    },

    async openAddressAttributionModal() {
      return this.runCaseModalOperation({
        title: 'Opening address attribution fill…',
        message: 'Scanning Main Data for missing address attribution targets.',
        target: 'address-attribution-modal',
        finishMessage: 'Address attribution fill view ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.addressAttributionPaste = '';
        this.addressAttributionStatus = '';
        this.addressAttributionLastRunSummary = null;
        this.showAddressAttributionModal = true;
        await this.yieldToCaseUi();
        this.refreshAddressAttributionRows();
        return true;
      });
    },

    cancelAddressAttributionModal() {
      if (this.addressAttributionRunning) return;
      this.showAddressAttributionModal = false;
    },

    clearAddressAttributionDraft() {
      this.addressAttributionPaste = '';
      this.refreshAddressAttributionRows();
    },

    buildAddressAttributionDraftRows() {
      const analysis = buildAddressAttributionAnalysis(this.getMatrixForLogicalSheet('main'));
      return analysis.missingTargets.map((target, index) => {
        const proposed = target.currentAttribution || '';
        const hasProposed = !!normalizeAttributionValue(proposed);
        return {
          id: target.id || `address-attribution-draft-${index}`,
          key: target.key,
          address: target.address,
          firstSpreadsheetRow: target.firstSpreadsheetRow,
          sideSummary: target.sideSummary,
          occurrenceCount: target.occurrences?.length || 0,
          missingOccurrenceCount: target.missingOccurrenceCount || 0,
          currentAttribution: target.currentAttribution,
          attributionDraft: proposed,
          include: true,
          target,
          status: hasProposed ? 'ready' : 'missing',
          statusLabel: hasProposed ? 'Ready' : 'Needs value',
          message: hasProposed
            ? 'An existing attribution for this address can be copied into remaining blank rows.'
            : 'Add attribution or Unknown.'
        };
      });
    },

    refreshAddressAttributionRows() {
      const previousDrafts = new Map((this.addressAttributionRows || []).map(row => [row.key, {
        attributionDraft: row.attributionDraft,
        include: row.include
      }]));

      this.addressAttributionRows = this.buildAddressAttributionDraftRows().map((row) => {
        const previous = previousDrafts.get(row.key);
        if (!previous) return row;
        const attributionDraft = normalizeAttributionValue(previous.attributionDraft) || row.attributionDraft;
        return {
          ...row,
          attributionDraft,
          include: previous.include !== false
        };
      });

      this.refreshAddressAttributionRowStatuses();
      this.addressAttributionStatus = this.addressAttributionStageSummary;
      this.refreshCaseSummary();
      return this.addressAttributionRows;
    },

    refreshAddressAttributionRowStatuses() {
      const seenLabels = new Map();
      this.addressAttributionRows = (this.addressAttributionRows || []).map((row) => {
        const attribution = normalizeAttributionValue(row.attributionDraft);
        const current = normalizeAttributionValue(row.currentAttribution);
        let status = 'missing';
        let statusLabel = 'Needs value';
        let message = 'Add attribution or Unknown.';

        if (attribution) {
          status = current && attribution.toLowerCase() === current.toLowerCase() ? 'propagate' : 'ready';
          statusLabel = status === 'propagate' ? 'Propagate' : 'Ready';
          message = status === 'propagate'
            ? 'Existing attribution will be copied into blank matching rows.'
            : 'This value will be applied to matching rows.';
        }

        const lower = attribution.toLowerCase();
        if (attribution && seenLabels.has(row.key) && seenLabels.get(row.key) !== lower) {
          message = 'Duplicate address appears with a different staged value; the last value applied will win.';
        }
        if (attribution) seenLabels.set(row.key, lower);

        return {
          ...row,
          attributionDraft: attribution,
          status,
          statusLabel,
          message
        };
      });
    },

    parseAddressAttributionPasteIntoRows() {
      const parsed = parseAddressAttributionTsv(this.addressAttributionPaste, this.addressAttributionRows);
      if (!String(this.addressAttributionPaste || '').trim()) {
        this.addressAttributionStatus = this.addressAttributionStageSummary;
        return parsed;
      }

      if (!parsed.hasMappings) {
        this.addressAttributionStatus = `No address-attribution mappings were parsed. ${parsed.skippedCount ? `${parsed.skippedCount} row${parsed.skippedCount === 1 ? '' : 's'} skipped.` : ''}`.trim();
        return parsed;
      }

      const mappingByKey = new Map();
      parsed.mappings.forEach((mapping) => {
        if (mapping.key) mappingByKey.set(mapping.key, mapping);
      });

      this.addressAttributionRows = this.addressAttributionRows.map((row) => {
        const mapping = mappingByKey.get(row.key);
        if (!mapping) return row;
        return {
          ...row,
          attributionDraft: normalizeAttributionValue(mapping.attribution),
          include: true
        };
      });

      const matched = this.addressAttributionRows.filter(row => mappingByKey.has(row.key)).length;
      const unmatched = parsed.mappings.filter(mapping => !this.addressAttributionRows.some(row => row.key === mapping.key)).length;
      this.refreshAddressAttributionRowStatuses();
      this.addressAttributionStatus = `${parsed.mappings.length} mapping${parsed.mappings.length === 1 ? '' : 's'} parsed · ${matched} matched current missing addresses${unmatched ? ` · ${unmatched} not currently missing` : ''}${parsed.duplicateCount ? ` · ${parsed.duplicateCount} duplicate address row${parsed.duplicateCount === 1 ? '' : 's'}` : ''}.`;
      return parsed;
    },

    handleAddressAttributionCellPaste(event, startIndex) {
      const text = event?.clipboardData?.getData('text/plain') || '';
      if (!text || (!text.includes('\n') && !text.includes('\t'))) return;

      event.preventDefault();
      const parsed = parseAddressAttributionTsv(text, this.addressAttributionRows);
      if (parsed.hasMappings && parsed.mappings.some(mapping => this.addressAttributionRows.some(row => row.key === mapping.key))) {
        const mappingByKey = new Map(parsed.mappings.map(mapping => [mapping.key, mapping]));
        this.addressAttributionRows = this.addressAttributionRows.map((row) => {
          const mapping = mappingByKey.get(row.key);
          return mapping
            ? { ...row, attributionDraft: normalizeAttributionValue(mapping.attribution), include: true }
            : row;
        });
      } else {
        const values = text
          .replace(/\r\n/g, '\n')
          .replace(/\r/g, '\n')
          .split(/\n|\t/)
          .map(value => normalizeAttributionValue(value))
          .filter(Boolean);

        this.addressAttributionRows = this.addressAttributionRows.map((row, index) => {
          const valueIndex = index - startIndex;
          return valueIndex >= 0 && valueIndex < values.length
            ? { ...row, attributionDraft: values[valueIndex], include: true }
            : row;
        });
      }

      this.refreshAddressAttributionRowStatuses();
      this.addressAttributionStatus = 'Pasted values were filled into the attribution grid.';
    },

    fillOutstandingAddressAttributionsAsUnknown() {
      this.addressAttributionRows = this.addressAttributionRows.map(row => ({
        ...row,
        attributionDraft: normalizeAttributionValue(row.attributionDraft) || 'Unknown',
        include: true
      }));
      this.refreshAddressAttributionRowStatuses();
      this.addressAttributionStatus = 'Outstanding staged addresses are set to Unknown. Apply to write Column K.';
    },

    copyMissingAddressAttributionTsv() {
      const tsv = buildAddressAttributionExportTsvFromTargets(this.addressAttributionRows.map(row => ({
        address: row.address,
        currentAttribution: row.attributionDraft || row.currentAttribution || ''
      })));

      if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(tsv)
          .then(() => {
            this.addressAttributionStatus = 'Missing address TSV copied to clipboard.';
          })
          .catch(() => {
            this.loadOutputRows(tsv.split('\n').map(line => line.split('\t')), {
              title: 'Missing address attribution TSV',
              detail: 'Clipboard copy failed, so the TSV was staged in Output for copying.',
              sourceActionId: 'copy_missing_address_attribution_tsv',
              target: 'audit-only'
            });
            this.addressAttributionStatus = 'Clipboard copy failed; TSV was staged in Output for copying.';
          });
      } else {
        this.loadOutputRows(tsv.split('\n').map(line => line.split('\t')), {
          title: 'Missing address attribution TSV',
          detail: 'Clipboard is not available, so the TSV was staged in Output for copying.',
          sourceActionId: 'copy_missing_address_attribution_tsv',
          target: 'audit-only'
        });
        this.addressAttributionStatus = 'Clipboard unavailable; TSV was staged in Output for copying.';
      }
    },

    buildAddressAttributionCheckResult(analysis = null) {
      const effectiveAnalysis = analysis || buildAddressAttributionAnalysis(this.getMatrixForLogicalSheet('main'));
      return this.buildCheckResult(
        'attribution',
        !effectiveAnalysis.hasAddresses ? 'warn' : effectiveAnalysis.ready ? 'pass' : 'fail',
        !effectiveAnalysis.hasAddresses
          ? 'No From or To addresses were found in Main Data yet.'
          : effectiveAnalysis.ready
            ? `${effectiveAnalysis.addressCount} unique From/To address${effectiveAnalysis.addressCount === 1 ? '' : 'es'} have Column K attribution or Unknown wherever they appear.`
            : `${effectiveAnalysis.missingCount} unique From/To address${effectiveAnalysis.missingCount === 1 ? '' : 'es'} and ${effectiveAnalysis.missingOccurrenceCount} address occurrence${effectiveAnalysis.missingOccurrenceCount === 1 ? '' : 's'} still need Column K attribution or Unknown.`,
        {
          addressCount: effectiveAnalysis.addressCount,
          completeCount: effectiveAnalysis.completeCount,
          missingCount: effectiveAnalysis.missingCount,
          occurrenceCount: effectiveAnalysis.occurrenceCount,
          missingOccurrenceCount: effectiveAnalysis.missingOccurrenceCount,
          missingRowCount: effectiveAnalysis.missingRowCount,
          unknownCount: effectiveAnalysis.unknownCount
        }
      );
    },

    refreshAddressAttributionCheckResult(analysis = null) {
      const result = this.buildAddressAttributionCheckResult(analysis);
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        attribution: result
      };
      this.persistCaseSupportState();
      this.refreshCaseSummary();
      return result;
    },

    applyAddressAttributionRows() {
      if (this.addressAttributionRunning) return;
      this.refreshAddressAttributionRowStatuses();

      const selected = this.addressAttributionSelectedRows.map(row => ({
        address: row.address,
        key: row.key,
        attribution: normalizeAttributionValue(row.attributionDraft)
      })).filter(row => row.key && row.attribution);

      if (selected.length === 0) {
        this.addressAttributionStatus = 'Select at least one address with attribution or Unknown.';
        return;
      }

      this.addressAttributionRunning = true;
      const mainSheet = this.findLogicalSheetMeta('main');
      const mainData = this.getMatrixForLogicalSheet('main');
      const analysisBefore = buildAddressAttributionAnalysis(mainData);
      const applyResult = applyAddressAttributionMappingsToMainMatrix(mainData, selected, {
        overwriteExisting: this.addressAttributionOverwriteExisting
      });

      if (mainSheet) {
        this.writeSheetStateById(mainSheet.id, {
          ...this.getSheetStateById(mainSheet.id),
          data: normalizeCaseMatrix(applyResult.data),
          frozenTop: 1
        });
      }

      if (applyResult.changed) {
        this.markCaseDataMutated('address_attribution_applied', {
          rowsUpdated: applyResult.updatedRowCount,
          mappingsApplied: applyResult.distinctInputMappings,
          overwriteExisting: this.addressAttributionOverwriteExisting
        });
        if (this.autoSaveEnabled) {
          this.saveCurrentProjectFile({ persistInput: true, persistOutput: false, markSavedAt: true });
        }
      }

      const analysisAfter = applyResult.analysisAfter || buildAddressAttributionAnalysis(applyResult.data);
      this.refreshAddressAttributionCheckResult(analysisAfter);
      this.recordWorkflowToolRun('fill_address_attribution_main_data', {
        mappingsApplied: applyResult.distinctInputMappings,
        rowsUpdated: applyResult.updatedRowCount,
        remainingMissing: analysisAfter.missingCount,
        remainingMissingOccurrences: analysisAfter.missingOccurrenceCount,
        overwriteExisting: this.addressAttributionOverwriteExisting
      });

      this.loadOutputRows(buildAddressAttributionAuditRows(analysisBefore, applyResult, analysisAfter), {
        title: 'Address attribution fill audit',
        detail: 'Audit only. Column K updates were applied directly to matching Main Data rows.',
        sourceActionId: 'fill_address_attribution_main_data',
        target: 'audit-only'
      });

      if (analysisAfter.ready) {
        this.recordWorkflowSignal(WORKFLOW_SIGNAL_TO_ADDRESS_ATTRIBUTION_APPLIED, 'fill_address_attribution_main_data', { silent: true });
      }

      this.addressAttributionLastRunSummary = {
        rowsUpdated: applyResult.updatedRowCount,
        mappingsApplied: applyResult.distinctInputMappings,
        remainingMissing: analysisAfter.missingCount,
        completedAtUtc: new Date().toISOString()
      };
      this.addressAttributionRunning = false;
      this.refreshAddressAttributionRows();
      this.showCaseToast(applyResult.updatedRowCount > 0
        ? `Applied attribution to ${applyResult.updatedRowCount} Main Data row${applyResult.updatedRowCount === 1 ? '' : 's'}.`
        : 'No Main Data rows changed; matching rows may already have attribution.'
      );
      this.addressAttributionStatus = analysisAfter.summary;
    },

    stageRawDataRequestPlaceholder(action, step) {
      const coverage = buildMainRawDataCoverage(
        this.getMatrixForLogicalSheet('main'),
        this.getMatrixForLogicalSheet('raw')
      );
      const missingRows = Array.isArray(coverage.missingRows) ? coverage.missingRows : [];
      const sourceLabel = action?.id === 'request_pathtracer_raw_data' ? 'PathTracer Web App' : 'WayFinder Desktop App';
      const rows = [
        ['Raw Data Request Placeholder', action?.label || action?.id || 'Request Raw Data', '', '', '', '', '', '', '', 'Future connector scaffold only. No case data was changed.'],
        ['Workflow Step', step?.title || 'Load raw data for every starting main transaction', '', '', '', '', '', '', '', 'The current web workflow expects the investigator to paste raw TSV into Raw Data or use the Paste Raw Data modal.'],
        ['Future Source', sourceLabel, 'Request Pattern', 'Queue patient request', 'Result Handling', 'Append returned TSV into Raw Data only', '', '', '', 'Main Data remains unchanged until the later Tool 68/sapling preparation step.'],
        ['Current Coverage', `${coverage.coveredCount}/${coverage.totalMainTransactions}`, 'Missing', coverage.missingCount, 'To mismatches', coverage.mismatchCount, 'Raw sets detected', coverage.rawSets?.length || 0],
        ['Main Row', 'Path Label', 'Transaction Hash', 'To Address', 'Coverage Status', 'Message'],
        ...missingRows.map(row => [
          row.spreadsheetRowNumber,
          row.pathLabel || '',
          row.hash || '',
          row.toAddress || '',
          row.statusLabel || row.status || '',
          row.message || ''
        ])
      ];

      this.recordWorkflowToolRun(action?.id || 'raw_data_request_placeholder', {
        placeholder: true,
        source: sourceLabel,
        totalMainTransactions: coverage.totalMainTransactions,
        coveredCount: coverage.coveredCount,
        missingCount: coverage.missingCount,
        mismatchCount: coverage.mismatchCount
      });

      this.loadOutputRows(rows, {
        title: `${action?.label || 'Raw Data Request'} placeholder`,
        detail: 'Placeholder/audit only. Future WayFinder or PathTracer connectors should append returned TSV into Raw Data, not Main Data.',
        sourceActionId: action?.id || 'raw_data_request_placeholder',
        target: 'audit-only'
      });
      this.scrollToSection('case-output');
      this.showCaseToast('Raw-data request placeholder staged in Output. Paste raw TSV manually for now.');
    },

    stageToolSlotOutput(action, step) {
      const rows = [
        ['Tool Slot', action.label || action.id, '', '', '', '', '', '', '', 'Adapter not connected yet'],
        ['Workflow Step', step?.title || '', '', '', '', '', '', '', '', 'This scaffold keeps tool buttons visible where the eventual Vue tool/composable will attach.'],
        ['Expected Pattern', 'Read main/raw/legend/metadata as needed', 'Write results to output buffer', 'User reviews', 'Accept to main/raw/legend/metadata'],
        ['Next Dev Pass', `Connect action id: ${action.id}`, '', '', '', '', '', '', '', 'No case data was mutated by this placeholder.']
      ];

      this.loadOutputRows(rows, {
        title: `${action.label || action.id} output scaffold`,
        detail: 'Placeholder output only. Connect the migrated tool before treating this as a completed workflow signal.',
        sourceActionId: action.id,
        target: 'output'
      });
      this.scrollToSection('case-output');
      this.showCaseToast('Tool slot staged in the output buffer. No completion signal was recorded.');
    },

    loadOutputRows(rows, reviewState = {}) {
      const data = normalizeCaseMatrix(rows, CASE_DEFAULT_ROWS, CASE_DEFAULT_COLS);
      this.clearCasePerformanceCache?.('misc');
      this.$refs.outputSpreadsheet?.loadData?.(data);
      this.markOutputBufferGenerated(reviewState);
      if (this.autoSaveEnabled) this.handleOutputSheetSaveRequest();
    },

    appendOutputToLogicalSheet(role) {
      if (this.outputReviewIsAuditOnly) {
        this.showCaseToast('This output is an audit trail, not rows to append into case data.');
        return;
      }

      const outputRows = this.getNonEmptyOutputRows();
      if (outputRows.length === 0) {
        this.showCaseToast('Output buffer is empty.');
        return;
      }

      const roleLabel = role === 'raw' ? 'Raw Data' : 'Main Data';
      const confirmed = window.confirm(`Append ${outputRows.length} non-empty output row(s) to ${roleLabel}? Review the output first; this will mutate the case spreadsheet.`);
      if (!confirmed) return;

      this.ensureCaseWorkbookScaffold({ silent: true });
      const targetSheet = this.findLogicalSheetMeta(role);
      if (!targetSheet) {
        this.showCaseToast(`Could not find ${roleLabel}.`);
        return;
      }

      const state = this.getSheetStateById(targetSheet.id);
      const data = normalizeCaseMatrix(state.data);
      if (!rowHasMeaningfulData(data[0])) {
        CASE_COLUMN_HEADERS.forEach((header, index) => {
          data[0][index] = header;
        });
      }

      const rowsToAppend = outputRows.filter((row, index) => {
        if (index === 0 && this.headerRowLooksCanonical(row)) return false;
        return rowHasMeaningfulData(row);
      });

      let lastNonEmptyRow = 0;
      data.forEach((row, index) => {
        if (rowHasMeaningfulData(row)) lastNonEmptyRow = index;
      });

      const startRow = Math.max(1, lastNonEmptyRow + 1);
      const neededRows = startRow + rowsToAppend.length;
      while (data.length < neededRows) {
        data.push(Array(CASE_DEFAULT_COLS).fill(''));
      }

      rowsToAppend.forEach((row, rowOffset) => {
        const normalizedRow = normalizeCaseMatrix([row], 1, CASE_DEFAULT_COLS)[0];
        data[startRow + rowOffset] = normalizedRow;
      });

      this.writeSheetStateById(targetSheet.id, {
        ...state,
        data,
        frozenTop: 1
      });

      this.markCaseDataMutated('output_appended_to_case_sheet', {
        targetRole: role,
        rows: rowsToAppend.length
      });
      this.setOutputReviewState({ status: 'reviewed', reviewedAtUtc: new Date().toISOString() });
      if (this.autoSaveEnabled) this.saveCurrentProjectFile({ persistInput: true, persistOutput: true, markSavedAt: true });
      this.activateLogicalSheet(role);
      this.showCaseToast(`Appended ${rowsToAppend.length} output row(s) to ${roleLabel}.`);
    }
};
